import * as THREE from 'three';
import { ATLAS_SIZE, TILE_SIZE, TILES_PER_ROW } from '../constants.js';

export const BlockId = {
  AIR: 0,
  GRASS: 1,
  DIRT: 2,
  STONE: 3,
  SAND: 4,
  WOOD: 5,
  LEAVES: 6,
  WATER: 7,
  BEDROCK: 8,
  SNOW_GRASS: 9,
  SANDSTONE: 10,
  CACTUS: 11,
  PLANKS: 12,
  COBBLESTONE: 13,
  GLASS: 14,
  WHEAT: 15,
  DOOR_CLOSED: 16,
  DOOR_OPEN: 17,
  // New blocks
  COAL_ORE: 18,
  IRON_ORE: 19,
  GOLD_ORE: 20,
  DIAMOND_ORE: 21,
  REDSTONE_ORE: 22,
  LAPIS_ORE: 23,
  EMERALD_ORE: 24,
  OBSIDIAN: 25,
  TORCH: 26,
  CRAFTING_TABLE: 27,
  FURNACE: 28,
  FURNACE_LIT: 29,
  CHEST: 30,
  TNT: 31,
  BOOKSHELF: 32,
  BRICKS: 33,
  IRON_BLOCK: 34,
  GOLD_BLOCK: 35,
  DIAMOND_BLOCK: 36,
  SLAB_STONE: 37,
  SLAB_WOOD: 38,
  FENCE: 39,
  LADDER: 40,
  FLOWER_RED: 41,
  FLOWER_YELLOW: 42,
  TALL_GRASS: 43,
  MUSHROOM_RED: 44,
  MUSHROOM_BROWN: 45,
  GRAVEL: 46,
  CLAY: 47,
  LOG_BIRCH: 48,
  LEAVES_BIRCH: 49,
  SNOW: 50,
  ICE: 51,
  PUMPKIN: 52,
  MELON: 53,
  WOOL_WHITE: 54,
  MOSS_STONE: 55,
  NETHERRACK: 56,
  GLOWSTONE: 57,
  SOUL_SAND: 58,
  NETHER_BRICKS: 59,
  END_STONE: 60,
  BED: 61,
  FARMLAND: 62,
  FARMLAND_WET: 63,
  // Biome blocks (jungle, swamp, savanna)
  JUNGLE_LOG: 64,
  JUNGLE_LEAVES: 65,
  ACACIA_LOG: 66,
  ACACIA_LEAVES: 67,
  VINE: 68,
  LILY_PAD: 69,
  ENCHANTING_TABLE: 70,
  BREWING_STAND: 71,
  // Nether dimension
  NETHER_PORTAL: 72,
  LAVA: 73,
  NETHER_GOLD_ORE: 74,
  NETHER_QUARTZ_ORE: 75,
  // Redstone system blocks
  REDSTONE_WIRE: 76,
  LEVER: 77,
  BUTTON: 78,
  PRESSURE_PLATE: 79,
  REDSTONE_TORCH: 80,
  REPEATER: 81,
  // Interactive blocks
  TRAPDOOR: 82,
  STAIRS_STONE: 83,
  STAIRS_WOOD: 84,
  SIGN: 85,
  JUKEBOX: 86,
  NOTEBLOCK: 87,
  // Grave system
  GRAVE_BASE: 88,
  COFFIN: 89,
  COFFIN_OPEN: 90,
  CROSS: 91,
  // End dimension
  END_PORTAL_FRAME: 92,
  END_PORTAL: 93,
  PURPUR_BLOCK: 94,
  PURPUR_PILLAR: 95,
  END_STONE_BRICKS: 96,
  CHORUS_PLANT: 97,
  DRAGON_EGG: 98,
  END_ROD: 99,
  // Farming & Village blocks (Fase 5)
  CARROT: 100,
  POTATO: 101,
  BEETROOT: 102,
  BERRY_BUSH: 103,
  PUMPKIN_STEM: 104,
  MELON_STEM: 105,
  SUGAR_CANE: 106,
  BELL: 107,
  // Redstone automation (Fase 6)
  PISTON: 108,
  PISTON_HEAD: 109,
  STICKY_PISTON: 110,
  HOPPER: 111,
  DROPPER: 112,
  DISPENSER: 113,
  OBSERVER: 114,
  REDSTONE_LAMP: 115,
  REDSTONE_LAMP_LIT: 116,
  DAYLIGHT_SENSOR: 117,
  POWERED_RAIL: 118,
  DETECTOR_RAIL: 119,
  RAIL: 120,
  // Exploration blocks (Fase 7)
  DRIPSTONE: 121,
  POINTED_DRIPSTONE: 122,
  AZALEA: 123,
  AZALEA_LEAVES: 124,
  GLOW_BERRY_VINE: 125,
  SCULK: 126,
  SCULK_SENSOR: 127,
  MOSS_BLOCK: 128,
  MYCELIUM: 129,
  TERRACOTTA: 130,
  TERRACOTTA_RED: 131,
  TERRACOTTA_ORANGE: 132,
  // Decorative blocks (Fase 8)
  WOOL_RED: 133,
  WOOL_BLUE: 134,
  WOOL_GREEN: 135,
  WOOL_YELLOW: 136,
  WOOL_BLACK: 137,
  WOOL_ORANGE: 138,
  WOOL_PURPLE: 139,
  GLASS_RED: 140,
  GLASS_BLUE: 141,
  GLASS_GREEN: 142,
  GLASS_YELLOW: 143,
  LANTERN: 144,
  CHAIN: 145,
  CAMPFIRE: 146,
  CAMPFIRE_LIT: 147,
  CANDLE: 148,
  SCAFFOLDING: 149,
  BARREL: 150,
  LOOM: 151,
  SMOKER: 152,
};

// Tile index assignments:
// 0: Grass top, 1: Grass side, 2: Dirt, 3: Stone, 4: Sand
// 5: Wood bark, 6: Wood top, 7: Leaves, 8: Water, 9: Bedrock
// 10: Snow top, 11: Snow grass side, 12: Cactus, 13: Sandstone, 14: Planks
// 15: Cobblestone, 16: Glass, 17: Wheat, 18: Door closed, 19: Door open
// 20: Coal ore, 21: Iron ore, 22: Gold ore, 23: Diamond ore, 24: Redstone ore
// 25: Lapis ore, 26: Emerald ore, 27: Obsidian, 28: Torch
// 29: Crafting table top, 30: Crafting table side, 31: Furnace top/stone
// 32: Furnace front, 33: Furnace side, 34: Furnace lit front
// 35: Chest top, 36: Chest front, 37: Chest side
// 38: TNT top, 39: TNT side, 40: TNT bottom
// 41: Bookshelf side, 42: Bricks, 43: Iron block, 44: Gold block
// 45: Diamond block, 46: Fence, 47: Ladder
// 48: Flower red, 49: Flower yellow, 50: Tall grass
// 51: Mushroom red, 52: Mushroom brown, 53: Gravel, 54: Clay
// 55: Birch bark, 56: Birch log top, 57: Birch leaves, 58: Snow block
// 59: Ice, 60: Pumpkin top, 61: Pumpkin side
// 62: Melon top, 63: Melon side, 64: Wool white, 65: Moss stone
// 66: Netherrack, 67: Glowstone, 68: Soul sand, 69: Nether bricks, 70: End stone
// 71: Crack stage 1, 72: Crack stage 2, 73: Crack stage 3, 74: Crack stage 4
// 75: Bed top (red), 76: Bed side (dark red/wood)
// 77: Farmland dry, 78: Farmland wet
// 79: Jungle log bark, 80: Jungle leaves, 81: Acacia log bark, 82: Acacia leaves
// 83: Vine, 84: Lily pad
// 85: Enchanting table top, 86: Enchanting table side
// 87: Nether portal (purple swirl), 88: Lava, 89: Nether gold ore, 90: Nether quartz ore
// 91: Redstone wire, 92: Lever, 93: Button, 94: Pressure plate, 95: Redstone torch, 96: Repeater
// 97: Trapdoor, 98: Stairs stone, 99: Stairs wood, 100: Sign
// 101: Jukebox top, 102: Jukebox side, 103: Noteblock
// 107: Coffin top (lid), 108: Coffin side (dark wood), 109: Coffin open top (red interior)
// 110: Cross texture, 111: Grave base top (dark dirt), 112: Coffin front
// 113: End portal frame top, 114: End portal frame side, 115: End portal (starfield)
// 116: Purpur block, 117: Purpur pillar top, 118: Purpur pillar side
// 119: End stone bricks, 120: Chorus plant, 121: Dragon egg, 122: End rod
// 123: Carrot, 124: Potato, 125: Beetroot, 126: Berry bush
// 127: Pumpkin stem, 128: Melon stem, 129: Sugar cane, 130: Bell
// 131: Piston face, 132: Piston bottom (planks), 133: Piston side (stone+wood)
// 134: Piston head (wood platform), 135: Sticky piston face (green slime)
// 136: Hopper (dark iron funnel), 137: Dropper/Dispenser top (cobblestone)
// 138: Dropper front (cobblestone with hole), 139: Dispenser front (cobblestone with mouth)
// 140: Observer top/bottom (stone with ridges), 141: Observer side (arrow pattern)
// 142: Redstone lamp off, 143: Redstone lamp lit
// 144: Daylight sensor (blue glass), 145: Powered rail (gold)
// 146: Detector rail (iron+redstone), 147: Rail (iron tracks)
// 148: Dripstone, 149: Pointed dripstone, 150: Azalea bush
// 151: Azalea leaves, 152: Glow berry vine, 153: Sculk
// 154: Sculk sensor, 155: Moss block, 156: Mycelium top
// 157: Terracotta, 158: Terracotta red, 159: Terracotta orange
// 160-166: Colored wool (red, blue, green, yellow, black, orange, purple)
// 167-170: Colored glass (red, blue, green, yellow)
// 171: Lantern, 172: Chain, 173: Campfire, 174: Campfire lit
// 175: Candle, 176: Scaffolding, 177: Barrel, 178: Loom, 179: Smoker

// [top, bottom, side] tile indices into the atlas
export const BlockFaces = {
  [BlockId.GRASS]:          [0, 2, 1],
  [BlockId.DIRT]:           [2, 2, 2],
  [BlockId.STONE]:          [3, 3, 3],
  [BlockId.SAND]:           [4, 4, 4],
  [BlockId.WOOD]:           [6, 6, 5],
  [BlockId.LEAVES]:         [7, 7, 7],
  [BlockId.WATER]:          [8, 8, 8],
  [BlockId.BEDROCK]:        [9, 9, 9],
  [BlockId.SNOW_GRASS]:     [10, 2, 11],
  [BlockId.SANDSTONE]:      [13, 13, 13],
  [BlockId.CACTUS]:         [12, 12, 12],
  [BlockId.PLANKS]:         [14, 14, 14],
  [BlockId.COBBLESTONE]:    [15, 15, 15],
  [BlockId.GLASS]:          [16, 16, 16],
  [BlockId.WHEAT]:          [17, 17, 17],
  [BlockId.DOOR_CLOSED]:    [18, 18, 18],
  [BlockId.DOOR_OPEN]:      [19, 19, 19],
  // New blocks
  [BlockId.COAL_ORE]:       [20, 20, 20],
  [BlockId.IRON_ORE]:       [21, 21, 21],
  [BlockId.GOLD_ORE]:       [22, 22, 22],
  [BlockId.DIAMOND_ORE]:    [23, 23, 23],
  [BlockId.REDSTONE_ORE]:   [24, 24, 24],
  [BlockId.LAPIS_ORE]:      [25, 25, 25],
  [BlockId.EMERALD_ORE]:    [26, 26, 26],
  [BlockId.OBSIDIAN]:       [27, 27, 27],
  [BlockId.TORCH]:          [28, 28, 28],
  [BlockId.CRAFTING_TABLE]: [29, 14, 30],  // top: grid, bottom: planks, side: tools
  [BlockId.FURNACE]:        [31, 31, 33],  // top: stone, bottom: stone, side: furnace side; front handled as side
  [BlockId.FURNACE_LIT]:    [31, 31, 34],  // same but lit front
  [BlockId.CHEST]:          [35, 35, 37],  // top: dark wood, bottom: same, side: wood
  [BlockId.TNT]:            [38, 40, 39],  // top: red pattern, bottom: tan, side: TNT label
  [BlockId.BOOKSHELF]:      [14, 14, 41],  // top/bottom: planks, side: books
  [BlockId.BRICKS]:         [42, 42, 42],
  [BlockId.IRON_BLOCK]:     [43, 43, 43],
  [BlockId.GOLD_BLOCK]:     [44, 44, 44],
  [BlockId.DIAMOND_BLOCK]:  [45, 45, 45],
  [BlockId.SLAB_STONE]:     [3, 3, 3],     // uses stone texture
  [BlockId.SLAB_WOOD]:      [14, 14, 14],  // uses planks texture
  [BlockId.FENCE]:          [46, 46, 46],
  [BlockId.LADDER]:         [47, 47, 47],
  [BlockId.FLOWER_RED]:     [48, 48, 48],
  [BlockId.FLOWER_YELLOW]:  [49, 49, 49],
  [BlockId.TALL_GRASS]:     [50, 50, 50],
  [BlockId.MUSHROOM_RED]:   [51, 51, 51],
  [BlockId.MUSHROOM_BROWN]: [52, 52, 52],
  [BlockId.GRAVEL]:         [53, 53, 53],
  [BlockId.CLAY]:           [54, 54, 54],
  [BlockId.LOG_BIRCH]:      [56, 56, 55],  // top: rings, bottom: rings, side: bark
  [BlockId.LEAVES_BIRCH]:   [57, 57, 57],
  [BlockId.SNOW]:           [58, 58, 58],
  [BlockId.ICE]:            [59, 59, 59],
  [BlockId.PUMPKIN]:        [60, 60, 61],  // top: stem, bottom: same, side: orange face
  [BlockId.MELON]:          [62, 62, 63],  // top: green, bottom: green, side: stripes
  [BlockId.WOOL_WHITE]:     [64, 64, 64],
  [BlockId.MOSS_STONE]:     [65, 65, 65],
  [BlockId.NETHERRACK]:     [66, 66, 66],
  [BlockId.GLOWSTONE]:      [67, 67, 67],
  [BlockId.SOUL_SAND]:      [68, 68, 68],
  [BlockId.NETHER_BRICKS]:  [69, 69, 69],
  [BlockId.END_STONE]:      [70, 70, 70],
  [BlockId.BED]:             [75, 14, 76],  // top: bed top, bottom: planks, side: bed side
  [BlockId.FARMLAND]:         [77, 2, 2],   // top: farmland dry, bottom: dirt, side: dirt
  [BlockId.FARMLAND_WET]:     [78, 2, 2],   // top: farmland wet, bottom: dirt, side: dirt
  // Biome blocks
  [BlockId.JUNGLE_LOG]:      [6, 6, 79],   // top: wood rings, bottom: wood rings, side: jungle bark
  [BlockId.JUNGLE_LEAVES]:   [80, 80, 80],
  [BlockId.ACACIA_LOG]:      [6, 6, 81],   // top: wood rings, bottom: wood rings, side: acacia bark
  [BlockId.ACACIA_LEAVES]:   [82, 82, 82],
  [BlockId.VINE]:            [83, 83, 83],
  [BlockId.LILY_PAD]:        [84, 84, 84],
  [BlockId.ENCHANTING_TABLE]: [85, 27, 86],  // top: enchant book, bottom: obsidian, side: enchant side
  [BlockId.BREWING_STAND]:    [31, 31, 33],  // top: stone, bottom: stone, side: furnace side (reused)
  // Nether dimension blocks
  [BlockId.NETHER_PORTAL]:    [87, 87, 87],
  [BlockId.LAVA]:             [88, 88, 88],
  [BlockId.NETHER_GOLD_ORE]:  [89, 89, 89],
  [BlockId.NETHER_QUARTZ_ORE]:[90, 90, 90],
  // Redstone system blocks
  [BlockId.REDSTONE_WIRE]:    [91, 91, 91],
  [BlockId.LEVER]:            [92, 92, 92],
  [BlockId.BUTTON]:           [93, 93, 93],
  [BlockId.PRESSURE_PLATE]:   [94, 94, 94],
  [BlockId.REDSTONE_TORCH]:   [95, 95, 95],
  [BlockId.REPEATER]:         [96, 96, 96],
  // Interactive blocks
  [BlockId.TRAPDOOR]:         [97, 97, 97],
  [BlockId.STAIRS_STONE]:     [98, 98, 98],
  [BlockId.STAIRS_WOOD]:      [99, 99, 99],
  [BlockId.SIGN]:             [100, 100, 100],
  [BlockId.JUKEBOX]:          [101, 14, 102],  // top: jukebox top, bottom: planks, side: jukebox side
  [BlockId.NOTEBLOCK]:        [103, 103, 103],
  // Grave system
  [BlockId.GRAVE_BASE]:       [111, 2, 2],      // top: dark grave dirt, bottom/side: dirt
  [BlockId.COFFIN]:           [107, 108, 108],   // top: coffin lid, bottom/side: dark wood
  [BlockId.COFFIN_OPEN]:      [109, 108, 108],   // top: open interior (red), rest: dark wood
  [BlockId.CROSS]:            [110, 110, 110],   // cross texture all sides
  // End dimension
  [BlockId.END_PORTAL_FRAME]: [113, 114, 114],   // top: frame with eye socket, side: carved stone
  [BlockId.END_PORTAL]:       [115, 115, 115],   // starfield portal
  [BlockId.PURPUR_BLOCK]:     [116, 116, 116],   // purple stone
  [BlockId.PURPUR_PILLAR]:    [117, 117, 118],   // top: rings, side: striped
  [BlockId.END_STONE_BRICKS]: [119, 119, 119],   // brick pattern
  [BlockId.CHORUS_PLANT]:     [120, 120, 120],   // purple organic
  [BlockId.DRAGON_EGG]:       [121, 121, 121],   // dark purple egg
  [BlockId.END_ROD]:          [122, 122, 122],   // glowing rod
  // Farming & Village blocks (Fase 5)
  [BlockId.CARROT]:            [123, 123, 123],
  [BlockId.POTATO]:            [124, 124, 124],
  [BlockId.BEETROOT]:          [125, 125, 125],
  [BlockId.BERRY_BUSH]:        [126, 126, 126],
  [BlockId.PUMPKIN_STEM]:      [127, 127, 127],
  [BlockId.MELON_STEM]:        [128, 128, 128],
  [BlockId.SUGAR_CANE]:        [129, 129, 129],
  [BlockId.BELL]:              [130, 130, 130],
  // Redstone automation (Fase 6)
  [BlockId.PISTON]:            [131, 132, 133],   // top: piston face, bottom: wood, side: stone+wood
  [BlockId.PISTON_HEAD]:       [134, 134, 134],   // wood platform
  [BlockId.STICKY_PISTON]:     [135, 132, 133],   // top: sticky green face, bottom: wood, side: stone+wood
  [BlockId.HOPPER]:            [136, 136, 136],   // dark iron funnel
  [BlockId.DROPPER]:           [137, 137, 138],   // top: cobblestone, side: cobblestone with hole
  [BlockId.DISPENSER]:         [137, 137, 139],   // top: cobblestone, side: cobblestone with mouth
  [BlockId.OBSERVER]:          [140, 140, 141],   // top: stone ridges, side: arrow pattern
  [BlockId.REDSTONE_LAMP]:     [142, 142, 142],   // glowstone-like, off
  [BlockId.REDSTONE_LAMP_LIT]: [143, 143, 143],   // glowstone-like, bright
  [BlockId.DAYLIGHT_SENSOR]:   [144, 14, 14],     // top: glass-like, bottom/side: planks
  [BlockId.POWERED_RAIL]:      [145, 145, 145],   // gold rail
  [BlockId.DETECTOR_RAIL]:     [146, 146, 146],   // stone rail
  [BlockId.RAIL]:              [147, 147, 147],   // iron rail
  // Exploration blocks (Fase 7)
  [BlockId.DRIPSTONE]:         [148, 148, 148],   // rough beige stone
  [BlockId.POINTED_DRIPSTONE]: [149, 149, 149],   // stalactite
  [BlockId.AZALEA]:            [150, 150, 150],   // green bush with flowers
  [BlockId.AZALEA_LEAVES]:     [151, 151, 151],   // light green + pink
  [BlockId.GLOW_BERRY_VINE]:   [152, 152, 152],   // vine with glow dots
  [BlockId.SCULK]:             [153, 153, 153],   // dark teal-blue
  [BlockId.SCULK_SENSOR]:      [154, 154, 154],   // sculk with tendrils
  [BlockId.MOSS_BLOCK]:        [155, 155, 155],   // rich green fuzzy
  [BlockId.MYCELIUM]:          [156, 2, 1],       // mycelium top, dirt bottom, grass-like side
  [BlockId.TERRACOTTA]:        [157, 157, 157],   // clay-brown
  [BlockId.TERRACOTTA_RED]:    [158, 158, 158],   // red tint
  [BlockId.TERRACOTTA_ORANGE]: [159, 159, 159],   // orange tint
  // Decorative blocks (Fase 8)
  [BlockId.WOOL_RED]:           [160, 160, 160],
  [BlockId.WOOL_BLUE]:          [161, 161, 161],
  [BlockId.WOOL_GREEN]:         [162, 162, 162],
  [BlockId.WOOL_YELLOW]:        [163, 163, 163],
  [BlockId.WOOL_BLACK]:         [164, 164, 164],
  [BlockId.WOOL_ORANGE]:        [165, 165, 165],
  [BlockId.WOOL_PURPLE]:        [166, 166, 166],
  [BlockId.GLASS_RED]:          [167, 167, 167],
  [BlockId.GLASS_BLUE]:         [168, 168, 168],
  [BlockId.GLASS_GREEN]:        [169, 169, 169],
  [BlockId.GLASS_YELLOW]:       [170, 170, 170],
  [BlockId.LANTERN]:            [171, 171, 171],
  [BlockId.CHAIN]:              [172, 172, 172],
  [BlockId.CAMPFIRE]:           [173, 173, 173],
  [BlockId.CAMPFIRE_LIT]:       [174, 174, 174],
  [BlockId.CANDLE]:             [175, 175, 175],
  [BlockId.SCAFFOLDING]:        [176, 176, 176],
  [BlockId.BARREL]:             [177, 177, 177],
  [BlockId.LOOM]:               [178, 178, 178],
  [BlockId.SMOKER]:             [179, 179, 179],
};

export function isTransparent(blockId) {
  return blockId === BlockId.AIR ||
         blockId === BlockId.WATER ||
         blockId === BlockId.LEAVES ||
         blockId === BlockId.GLASS ||
         blockId === BlockId.DOOR_OPEN ||
         blockId === BlockId.TORCH ||
         blockId === BlockId.FLOWER_RED ||
         blockId === BlockId.FLOWER_YELLOW ||
         blockId === BlockId.TALL_GRASS ||
         blockId === BlockId.MUSHROOM_RED ||
         blockId === BlockId.MUSHROOM_BROWN ||
         blockId === BlockId.LADDER ||
         blockId === BlockId.ICE ||
         blockId === BlockId.FENCE ||
         blockId === BlockId.LEAVES_BIRCH ||
         blockId === BlockId.JUNGLE_LEAVES ||
         blockId === BlockId.ACACIA_LEAVES ||
         blockId === BlockId.VINE ||
         blockId === BlockId.LILY_PAD ||
         blockId === BlockId.NETHER_PORTAL ||
         blockId === BlockId.LAVA ||
         blockId === BlockId.REDSTONE_WIRE ||
         blockId === BlockId.PRESSURE_PLATE ||
         blockId === BlockId.REDSTONE_TORCH ||
         blockId === BlockId.TRAPDOOR ||
         blockId === BlockId.SIGN ||
         blockId === BlockId.STAIRS_STONE ||
         blockId === BlockId.STAIRS_WOOD ||
         blockId === BlockId.CROSS ||
         blockId === BlockId.END_PORTAL ||
         blockId === BlockId.CHORUS_PLANT ||
         blockId === BlockId.END_ROD ||
         blockId === BlockId.CARROT ||
         blockId === BlockId.POTATO ||
         blockId === BlockId.BEETROOT ||
         blockId === BlockId.BERRY_BUSH ||
         blockId === BlockId.PUMPKIN_STEM ||
         blockId === BlockId.MELON_STEM ||
         blockId === BlockId.SUGAR_CANE ||
         blockId === BlockId.PISTON_HEAD ||
         blockId === BlockId.RAIL ||
         blockId === BlockId.POWERED_RAIL ||
         blockId === BlockId.DETECTOR_RAIL ||
         blockId === BlockId.DAYLIGHT_SENSOR ||
         blockId === BlockId.AZALEA ||
         blockId === BlockId.GLOW_BERRY_VINE ||
         blockId === BlockId.GLASS_RED ||
         blockId === BlockId.GLASS_BLUE ||
         blockId === BlockId.GLASS_GREEN ||
         blockId === BlockId.GLASS_YELLOW ||
         blockId === BlockId.LANTERN ||
         blockId === BlockId.CHAIN ||
         blockId === BlockId.CAMPFIRE_LIT ||
         blockId === BlockId.CANDLE ||
         blockId === BlockId.SCAFFOLDING;
}

export function isPassable(blockId) {
  return blockId === BlockId.AIR ||
         blockId === BlockId.WATER ||
         blockId === BlockId.WHEAT ||
         blockId === BlockId.DOOR_OPEN ||
         blockId === BlockId.TORCH ||
         blockId === BlockId.FLOWER_RED ||
         blockId === BlockId.FLOWER_YELLOW ||
         blockId === BlockId.TALL_GRASS ||
         blockId === BlockId.MUSHROOM_RED ||
         blockId === BlockId.MUSHROOM_BROWN ||
         blockId === BlockId.LADDER ||
         blockId === BlockId.VINE ||
         blockId === BlockId.LILY_PAD ||
         blockId === BlockId.NETHER_PORTAL ||
         blockId === BlockId.LAVA ||
         blockId === BlockId.REDSTONE_WIRE ||
         blockId === BlockId.PRESSURE_PLATE ||
         blockId === BlockId.REDSTONE_TORCH ||
         blockId === BlockId.SIGN ||
         blockId === BlockId.CROSS ||
         blockId === BlockId.END_PORTAL ||
         blockId === BlockId.CHORUS_PLANT ||
         blockId === BlockId.END_ROD ||
         blockId === BlockId.CARROT ||
         blockId === BlockId.POTATO ||
         blockId === BlockId.BEETROOT ||
         blockId === BlockId.BERRY_BUSH ||
         blockId === BlockId.PUMPKIN_STEM ||
         blockId === BlockId.MELON_STEM ||
         blockId === BlockId.SUGAR_CANE ||
         blockId === BlockId.RAIL ||
         blockId === BlockId.POWERED_RAIL ||
         blockId === BlockId.DETECTOR_RAIL ||
         blockId === BlockId.DAYLIGHT_SENSOR ||
         blockId === BlockId.AZALEA ||
         blockId === BlockId.GLOW_BERRY_VINE ||
         blockId === BlockId.CHAIN ||
         blockId === BlockId.CANDLE ||
         blockId === BlockId.SCAFFOLDING;
}

export function isDoor(blockId) {
  return blockId === BlockId.DOOR_CLOSED || blockId === BlockId.DOOR_OPEN;
}

export function isRedstoneComponent(blockId) {
  return blockId === BlockId.REDSTONE_WIRE ||
         blockId === BlockId.LEVER ||
         blockId === BlockId.BUTTON ||
         blockId === BlockId.PRESSURE_PLATE ||
         blockId === BlockId.REDSTONE_TORCH ||
         blockId === BlockId.REPEATER ||
         blockId === BlockId.OBSERVER ||
         blockId === BlockId.REDSTONE_LAMP ||
         blockId === BlockId.REDSTONE_LAMP_LIT ||
         blockId === BlockId.DAYLIGHT_SENSOR ||
         blockId === BlockId.DISPENSER ||
         blockId === BlockId.DROPPER ||
         blockId === BlockId.PISTON ||
         blockId === BlockId.STICKY_PISTON ||
         blockId === BlockId.SCULK_SENSOR;
}

export function isLightSource(blockId) {
  switch (blockId) {
    case BlockId.TORCH:          return 14;
    case BlockId.GLOWSTONE:      return 15;
    case BlockId.WATER:          return 0;
    case BlockId.FURNACE_LIT:    return 13;
    case BlockId.LAVA:           return 15;
    case BlockId.NETHER_PORTAL:  return 11;
    case BlockId.REDSTONE_TORCH: return 7;
    case BlockId.END_PORTAL:     return 15;
    case BlockId.END_ROD:        return 14;
    case BlockId.REDSTONE_LAMP_LIT: return 15;
    case BlockId.GLOW_BERRY_VINE: return 14;
    case BlockId.LANTERN:        return 15;
    case BlockId.CAMPFIRE_LIT:   return 15;
    case BlockId.CANDLE:         return 10;
    default:                     return 0;
  }
}

export function getBlockHardness(blockId) {
  switch (blockId) {
    case BlockId.BEDROCK:        return Infinity;
    case BlockId.OBSIDIAN:       return 50;
    case BlockId.ENCHANTING_TABLE: return 5;
    case BlockId.BREWING_STAND:    return 3;
    case BlockId.DIAMOND_BLOCK:  return 5;
    case BlockId.IRON_BLOCK:     return 5;
    case BlockId.GOLD_BLOCK:     return 3;
    case BlockId.COAL_ORE:
    case BlockId.IRON_ORE:
    case BlockId.GOLD_ORE:
    case BlockId.DIAMOND_ORE:
    case BlockId.REDSTONE_ORE:
    case BlockId.LAPIS_ORE:
    case BlockId.EMERALD_ORE:    return 3;
    case BlockId.STONE:
    case BlockId.COBBLESTONE:
    case BlockId.BRICKS:
    case BlockId.NETHER_BRICKS:
    case BlockId.END_STONE:
    case BlockId.MOSS_STONE:
    case BlockId.SLAB_STONE:
    case BlockId.STAIRS_STONE:   return 1.5;
    case BlockId.WOOD:
    case BlockId.LOG_BIRCH:
    case BlockId.JUNGLE_LOG:
    case BlockId.ACACIA_LOG:
    case BlockId.PLANKS:
    case BlockId.SLAB_WOOD:
    case BlockId.CHEST:
    case BlockId.CRAFTING_TABLE:
    case BlockId.BOOKSHELF:
    case BlockId.FENCE:
    case BlockId.LADDER:
    case BlockId.PUMPKIN:
    case BlockId.MELON:
    case BlockId.TRAPDOOR:
    case BlockId.STAIRS_WOOD:
    case BlockId.JUKEBOX:
    case BlockId.NOTEBLOCK:      return 2;
    case BlockId.FARMLAND:
    case BlockId.FARMLAND_WET:   return 0.6;
    case BlockId.DIRT:
    case BlockId.SAND:
    case BlockId.GRAVEL:
    case BlockId.CLAY:
    case BlockId.SNOW:
    case BlockId.SOUL_SAND:      return 0.5;
    case BlockId.GLASS:
    case BlockId.GLOWSTONE:
    case BlockId.ICE:            return 0.3;
    case BlockId.LEAVES:
    case BlockId.LEAVES_BIRCH:
    case BlockId.JUNGLE_LEAVES:
    case BlockId.ACACIA_LEAVES:  return 0.2;
    case BlockId.BED:            return 0.2;
    case BlockId.GRAVE_BASE:     return 0.5;
    case BlockId.COFFIN:
    case BlockId.COFFIN_OPEN:    return 2;
    case BlockId.CROSS:          return 1;
    case BlockId.END_PORTAL_FRAME: return Infinity;
    case BlockId.END_PORTAL:     return Infinity;
    case BlockId.PURPUR_BLOCK:
    case BlockId.PURPUR_PILLAR:
    case BlockId.END_STONE_BRICKS: return 1.5;
    case BlockId.DRAGON_EGG:     return 3;
    case BlockId.CHORUS_PLANT:   return 0.4;
    case BlockId.END_ROD:        return 0;
    case BlockId.SIGN:           return 1;
    case BlockId.WHEAT:
    case BlockId.FLOWER_RED:
    case BlockId.FLOWER_YELLOW:
    case BlockId.MUSHROOM_RED:
    case BlockId.MUSHROOM_BROWN:
    case BlockId.TALL_GRASS:
    case BlockId.TORCH:
    case BlockId.TNT:
    case BlockId.VINE:
    case BlockId.LILY_PAD:
    case BlockId.REDSTONE_WIRE:
    case BlockId.LEVER:
    case BlockId.BUTTON:
    case BlockId.PRESSURE_PLATE:
    case BlockId.REDSTONE_TORCH:
    case BlockId.REPEATER:
    case BlockId.CARROT:
    case BlockId.POTATO:
    case BlockId.BEETROOT:
    case BlockId.BERRY_BUSH:
    case BlockId.PUMPKIN_STEM:
    case BlockId.MELON_STEM:
    case BlockId.SUGAR_CANE:     return 0;
    case BlockId.BELL:           return 3;
    case BlockId.PISTON:
    case BlockId.STICKY_PISTON:  return 1.5;
    case BlockId.HOPPER:         return 3;
    case BlockId.DROPPER:
    case BlockId.DISPENSER:      return 3.5;
    case BlockId.OBSERVER:       return 3;
    case BlockId.REDSTONE_LAMP:
    case BlockId.REDSTONE_LAMP_LIT: return 0.3;
    case BlockId.DAYLIGHT_SENSOR: return 0.2;
    case BlockId.POWERED_RAIL:
    case BlockId.DETECTOR_RAIL:
    case BlockId.RAIL:           return 0.7;
    case BlockId.PISTON_HEAD:    return 1.5;
    case BlockId.FURNACE:
    case BlockId.FURNACE_LIT:
    case BlockId.NETHERRACK:
    case BlockId.NETHER_GOLD_ORE:
    case BlockId.NETHER_QUARTZ_ORE:
    case BlockId.SANDSTONE:      return 1.5;
    case BlockId.NETHER_PORTAL:  return Infinity; // can't be mined
    case BlockId.LAVA:           return Infinity; // can't be mined
    // Exploration blocks (Fase 7)
    case BlockId.DRIPSTONE:
    case BlockId.POINTED_DRIPSTONE: return 1.5;
    case BlockId.AZALEA:         return 0;
    case BlockId.AZALEA_LEAVES:  return 0.2;
    case BlockId.GLOW_BERRY_VINE: return 0;
    case BlockId.SCULK:          return 0.2;
    case BlockId.SCULK_SENSOR:   return 1.5;
    case BlockId.MOSS_BLOCK:     return 0.1;
    case BlockId.MYCELIUM:       return 0.6;
    case BlockId.TERRACOTTA:
    case BlockId.TERRACOTTA_RED:
    case BlockId.TERRACOTTA_ORANGE: return 1.25;
    // Decorative blocks (Fase 8)
    case BlockId.WOOL_RED:
    case BlockId.WOOL_BLUE:
    case BlockId.WOOL_GREEN:
    case BlockId.WOOL_YELLOW:
    case BlockId.WOOL_BLACK:
    case BlockId.WOOL_ORANGE:
    case BlockId.WOOL_PURPLE:      return 0.8;
    case BlockId.GLASS_RED:
    case BlockId.GLASS_BLUE:
    case BlockId.GLASS_GREEN:
    case BlockId.GLASS_YELLOW:     return 0.3;
    case BlockId.LANTERN:          return 3.5;
    case BlockId.CHAIN:            return 5;
    case BlockId.CAMPFIRE:
    case BlockId.CAMPFIRE_LIT:     return 2;
    case BlockId.CANDLE:           return 0;
    case BlockId.SCAFFOLDING:      return 0;
    case BlockId.BARREL:
    case BlockId.LOOM:
    case BlockId.SMOKER:           return 2.5;
    default:                     return 1;
  }
}

export function getToolType(blockId) {
  switch (blockId) {
    // Pickaxe blocks
    case BlockId.STONE:
    case BlockId.COBBLESTONE:
    case BlockId.COAL_ORE:
    case BlockId.IRON_ORE:
    case BlockId.GOLD_ORE:
    case BlockId.DIAMOND_ORE:
    case BlockId.REDSTONE_ORE:
    case BlockId.LAPIS_ORE:
    case BlockId.EMERALD_ORE:
    case BlockId.OBSIDIAN:
    case BlockId.BRICKS:
    case BlockId.FURNACE:
    case BlockId.FURNACE_LIT:
    case BlockId.NETHERRACK:
    case BlockId.NETHER_BRICKS:
    case BlockId.NETHER_GOLD_ORE:
    case BlockId.NETHER_QUARTZ_ORE:
    case BlockId.END_STONE:
    case BlockId.PURPUR_BLOCK:
    case BlockId.PURPUR_PILLAR:
    case BlockId.END_STONE_BRICKS:
    case BlockId.DRAGON_EGG:
    case BlockId.ICE:
    case BlockId.IRON_BLOCK:
    case BlockId.GOLD_BLOCK:
    case BlockId.DIAMOND_BLOCK:
    case BlockId.SANDSTONE:
    case BlockId.MOSS_STONE:
    case BlockId.SLAB_STONE:
    case BlockId.ENCHANTING_TABLE:
    case BlockId.BREWING_STAND:
    case BlockId.BEDROCK:
    case BlockId.STAIRS_STONE:
      return 'pickaxe';
    // Shovel blocks
    case BlockId.DIRT:
    case BlockId.GRASS:
    case BlockId.SNOW_GRASS:
    case BlockId.SAND:
    case BlockId.GRAVEL:
    case BlockId.CLAY:
    case BlockId.SNOW:
    case BlockId.SOUL_SAND:
    case BlockId.FARMLAND:
    case BlockId.FARMLAND_WET:
      return 'shovel';
    // Axe blocks
    case BlockId.WOOD:
    case BlockId.LOG_BIRCH:
    case BlockId.JUNGLE_LOG:
    case BlockId.ACACIA_LOG:
    case BlockId.PLANKS:
    case BlockId.SLAB_WOOD:
    case BlockId.CHEST:
    case BlockId.CRAFTING_TABLE:
    case BlockId.BOOKSHELF:
    case BlockId.FENCE:
    case BlockId.LADDER:
    case BlockId.PUMPKIN:
    case BlockId.MELON:
    case BlockId.DOOR_CLOSED:
    case BlockId.DOOR_OPEN:
    case BlockId.TRAPDOOR:
    case BlockId.STAIRS_WOOD:
    case BlockId.SIGN:
    case BlockId.JUKEBOX:
    case BlockId.NOTEBLOCK:
      return 'axe';
    // Sword is best for plants
    case BlockId.LEAVES:
    case BlockId.LEAVES_BIRCH:
    case BlockId.JUNGLE_LEAVES:
    case BlockId.ACACIA_LEAVES:
    case BlockId.WHEAT:
    case BlockId.TALL_GRASS:
    case BlockId.VINE:
    case BlockId.LILY_PAD:
    case BlockId.CACTUS:
      return 'sword';
    default:
      return 'none';
  }
}

// Nivel de cosecha mínimo para minar un bloque
// 0 = mano, 1 = pico de madera, 2 = pico de piedra, 3 = pico de hierro, 4 = pico de diamante
export function getHarvestLevel(blockId) {
  switch (blockId) {
    // Nivel 0 - se puede romper con la mano
    case BlockId.DIRT:
    case BlockId.GRASS:
    case BlockId.SNOW_GRASS:
    case BlockId.SAND:
    case BlockId.GRAVEL:
    case BlockId.CLAY:
    case BlockId.SNOW:
    case BlockId.SOUL_SAND:
    case BlockId.WOOD:
    case BlockId.LOG_BIRCH:
    case BlockId.JUNGLE_LOG:
    case BlockId.ACACIA_LOG:
    case BlockId.LEAVES:
    case BlockId.LEAVES_BIRCH:
    case BlockId.JUNGLE_LEAVES:
    case BlockId.ACACIA_LEAVES:
    case BlockId.VINE:
    case BlockId.LILY_PAD:
    case BlockId.PLANKS:
    case BlockId.SLAB_WOOD:
    case BlockId.CRAFTING_TABLE:
    case BlockId.CHEST:
    case BlockId.BOOKSHELF:
    case BlockId.FENCE:
    case BlockId.LADDER:
    case BlockId.PUMPKIN:
    case BlockId.MELON:
    case BlockId.WHEAT:
    case BlockId.TALL_GRASS:
    case BlockId.FLOWER_RED:
    case BlockId.FLOWER_YELLOW:
    case BlockId.MUSHROOM_RED:
    case BlockId.MUSHROOM_BROWN:
    case BlockId.TORCH:
    case BlockId.TNT:
    case BlockId.GLASS:
    case BlockId.GLOWSTONE:
    case BlockId.ICE:
    case BlockId.CACTUS:
    case BlockId.WOOL_WHITE:
    case BlockId.DOOR_CLOSED:
    case BlockId.DOOR_OPEN:
    case BlockId.NETHERRACK:
    case BlockId.FARMLAND:
    case BlockId.FARMLAND_WET:
    case BlockId.NETHER_QUARTZ_ORE:
    case BlockId.REDSTONE_WIRE:
    case BlockId.LEVER:
    case BlockId.BUTTON:
    case BlockId.PRESSURE_PLATE:
    case BlockId.REDSTONE_TORCH:
    case BlockId.REPEATER:
    case BlockId.TRAPDOOR:
    case BlockId.STAIRS_STONE:
    case BlockId.STAIRS_WOOD:
    case BlockId.SIGN:
    case BlockId.JUKEBOX:
    case BlockId.NOTEBLOCK:
      return 0;
    // Nivel 1 - pico de madera
    case BlockId.STONE:
    case BlockId.COBBLESTONE:
    case BlockId.SANDSTONE:
    case BlockId.MOSS_STONE:
    case BlockId.SLAB_STONE:
    case BlockId.COAL_ORE:
    case BlockId.BRICKS:
    case BlockId.NETHER_BRICKS:
    case BlockId.END_STONE:
    case BlockId.FURNACE:
    case BlockId.FURNACE_LIT:
    case BlockId.ENCHANTING_TABLE:
    case BlockId.BREWING_STAND:
    case BlockId.NETHER_GOLD_ORE:
      return 1;
    // Nivel 2 - pico de piedra
    case BlockId.IRON_ORE:
    case BlockId.LAPIS_ORE:
    case BlockId.IRON_BLOCK:
      return 2;
    // Nivel 3 - pico de hierro
    case BlockId.GOLD_ORE:
    case BlockId.DIAMOND_ORE:
    case BlockId.REDSTONE_ORE:
    case BlockId.EMERALD_ORE:
    case BlockId.GOLD_BLOCK:
    case BlockId.DIAMOND_BLOCK:
    case BlockId.OBSIDIAN:
      return 3;
    // Bedrock - irrompible
    case BlockId.BEDROCK:
      return Infinity;
    default:
      return 0;
  }
}

// Tipo de herramienta requerida para obtener drops del bloque
// Retorna 'pickaxe', 'shovel', 'axe', o null (cualquier cosa sirve)
export function getRequiredToolType(blockId) {
  switch (blockId) {
    // Pico requerido - piedra, minerales, ladrillos
    case BlockId.STONE:
    case BlockId.COBBLESTONE:
    case BlockId.SANDSTONE:
    case BlockId.MOSS_STONE:
    case BlockId.SLAB_STONE:
    case BlockId.COAL_ORE:
    case BlockId.IRON_ORE:
    case BlockId.GOLD_ORE:
    case BlockId.DIAMOND_ORE:
    case BlockId.REDSTONE_ORE:
    case BlockId.LAPIS_ORE:
    case BlockId.EMERALD_ORE:
    case BlockId.OBSIDIAN:
    case BlockId.BRICKS:
    case BlockId.NETHER_BRICKS:
    case BlockId.NETHER_GOLD_ORE:
    case BlockId.NETHER_QUARTZ_ORE:
    case BlockId.END_STONE:
    case BlockId.FURNACE:
    case BlockId.FURNACE_LIT:
    case BlockId.IRON_BLOCK:
    case BlockId.GOLD_BLOCK:
    case BlockId.DIAMOND_BLOCK:
    case BlockId.NETHERRACK:
    case BlockId.ENCHANTING_TABLE:
    case BlockId.BREWING_STAND:
    case BlockId.STAIRS_STONE:
      return 'pickaxe';
    // Pala requerida - tierra, arena, grava, nieve, arcilla
    case BlockId.DIRT:
    case BlockId.GRASS:
    case BlockId.SNOW_GRASS:
    case BlockId.SAND:
    case BlockId.GRAVEL:
    case BlockId.CLAY:
    case BlockId.SNOW:
    case BlockId.SOUL_SAND:
    case BlockId.FARMLAND:
    case BlockId.FARMLAND_WET:
      return 'shovel';
    // Hacha requerida - madera, tablas, cofre, mesa
    case BlockId.WOOD:
    case BlockId.LOG_BIRCH:
    case BlockId.JUNGLE_LOG:
    case BlockId.ACACIA_LOG:
    case BlockId.PLANKS:
    case BlockId.SLAB_WOOD:
    case BlockId.CRAFTING_TABLE:
    case BlockId.CHEST:
    case BlockId.BOOKSHELF:
    case BlockId.FENCE:
    case BlockId.DOOR_CLOSED:
    case BlockId.DOOR_OPEN:
    case BlockId.TRAPDOOR:
    case BlockId.STAIRS_WOOD:
    case BlockId.SIGN:
    case BlockId.JUKEBOX:
    case BlockId.NOTEBLOCK:
      return 'axe';
    // null = cualquier herramienta sirve
    default:
      return null;
  }
}

// Índices de tiles para overlay de grietas (4 etapas)
export const CRACK_TILES = [71, 72, 73, 74];

// Get UV coordinates for a tile index
export function getTileUV(tileIndex) {
  const col = tileIndex % TILES_PER_ROW;
  const row = Math.floor(tileIndex / TILES_PER_ROW);
  const pad = 0.5 / ATLAS_SIZE;
  return {
    u0: col / TILES_PER_ROW + pad,
    v0: 1 - (row + 1) / TILES_PER_ROW + pad,
    u1: (col + 1) / TILES_PER_ROW - pad,
    v1: 1 - row / TILES_PER_ROW - pad,
  };
}

/** Map wheat growth stage (0-3) to tile index */
const WHEAT_STAGE_TILES = [104, 105, 106, 17];
export function getWheatTileForStage(stage) {
  return WHEAT_STAGE_TILES[stage] || 17;
}

// Seeded random for texture generation (deterministic)
function seededRandom(seed) {
  let s = seed;
  return function() {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

// Helper: draw stone base then colored ore specks
function drawOre(ctx, s, r, speckColor, speckColor2) {
  // Stone base
  ctx.fillStyle = '#8A8A8A';
  ctx.fillRect(0, 0, s, s);
  for (let i = 0; i < 40; i++) {
    const shade = r();
    ctx.fillStyle = shade > 0.6 ? '#7A7A7A' : shade > 0.3 ? '#9A9A9A' : '#6A6A6A';
    ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), r() > 0.5 ? 2 : 1, 1);
  }
  // Ore specks - clusters of colored pixels
  for (let i = 0; i < 8; i++) {
    const cx = Math.floor(r() * (s - 2)) + 1;
    const cy = Math.floor(r() * (s - 2)) + 1;
    ctx.fillStyle = r() > 0.5 ? speckColor : speckColor2;
    ctx.fillRect(cx, cy, 2, 2);
    ctx.fillRect(cx + (r() > 0.5 ? 1 : -1), cy, 1, 1);
  }
}

export function generateAtlasTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_SIZE;
  canvas.height = ATLAS_SIZE;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, ATLAS_SIZE, ATLAS_SIZE);

  const rng = seededRandom(42);

  function drawTile(index, drawFn) {
    const col = index % TILES_PER_ROW;
    const row = Math.floor(index / TILES_PER_ROW);
    const x = col * TILE_SIZE;
    const y = row * TILE_SIZE;
    ctx.save();
    ctx.translate(x, y);
    drawFn(ctx, TILE_SIZE, rng);
    ctx.restore();
  }

  // =============================================
  // EXISTING TILES 0-19 (unchanged)
  // =============================================

  // Tile 0: Grass top
  drawTile(0, (ctx, s, r) => {
    ctx.fillStyle = '#5B8C2A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#4A7A20' : '#6B9C3A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 1: Grass side
  drawTile(1, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A5A30' : '#9C7C4E';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    ctx.fillStyle = '#5B8C2A';
    ctx.fillRect(0, 0, s, 3);
    ctx.fillStyle = '#4A7A20';
    for (let x = 0; x < s; x++) {
      const h = r() > 0.5 ? 4 : 3;
      ctx.fillRect(x, 0, 1, h);
    }
  });

  // Tile 2: Dirt
  drawTile(2, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A5A30' : '#9C7C4E';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 3: Stone
  drawTile(3, (ctx, s, r) => {
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 50; i++) {
      const shade = r();
      ctx.fillStyle = shade > 0.6 ? '#7A7A7A' : shade > 0.3 ? '#9A9A9A' : '#6A6A6A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), r() > 0.5 ? 2 : 1, 1);
    }
  });

  // Tile 4: Sand
  drawTile(4, (ctx, s, r) => {
    ctx.fillStyle = '#D4C483';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#C4B473' : '#E4D493';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 5: Wood side (bark)
  drawTile(5, (ctx, s, r) => {
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 2) {
      ctx.fillStyle = y % 4 === 0 ? '#5A3F1E' : '#7B5F3E';
      ctx.fillRect(0, y, s, 1);
    }
  });

  // Tile 6: Wood top (rings)
  drawTile(6, (ctx, s, r) => {
    ctx.fillStyle = '#B89B60';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = '#A08050';
    ctx.lineWidth = 1;
    const cx = s / 2, cy = s / 2;
    for (let rad = 2; rad < s / 2; rad += 2) {
      ctx.beginPath();
      ctx.arc(cx, cy, rad, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(cx - 1, cy - 1, 2, 2);
  });

  // Tile 7: Leaves
  drawTile(7, (ctx, s, r) => {
    ctx.fillStyle = '#3A7A1A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#2A6A10' : '#4A8A2A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
  });

  // Tile 8: Water — deep blue with layered wave patterns and color depth
  drawTile(8, (ctx, s, r) => {
    // Deep water base gradient (dark at bottom, lighter at top)
    const grad = ctx.createLinearGradient(0, 0, 0, s);
    grad.addColorStop(0, '#4488CC');
    grad.addColorStop(0.5, '#336699');
    grad.addColorStop(1, '#224477');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, s, s);

    // Subtle horizontal wave bands (varying brightness)
    for (let row = 0; row < s; row++) {
      const wave = Math.sin(row * 0.8 + r() * 2) * 0.12;
      const alpha = 0.08 + wave;
      if (alpha > 0) {
        ctx.fillStyle = `rgba(100, 180, 255, ${alpha.toFixed(3)})`;
        ctx.fillRect(0, row, s, 1);
      }
    }

    // Scattered light caustic highlights
    for (let i = 0; i < 12; i++) {
      const cx = Math.floor(r() * s);
      const cy = Math.floor(r() * s);
      const w = 1 + Math.floor(r() * 3);
      ctx.fillStyle = `rgba(150, 210, 255, ${(0.15 + r() * 0.2).toFixed(2)})`;
      ctx.fillRect(cx, cy, w, 1);
    }

    // Darker depth pockets
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = `rgba(20, 40, 80, ${(0.1 + r() * 0.15).toFixed(2)})`;
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 9: Bedrock
  drawTile(9, (ctx, s, r) => {
    ctx.fillStyle = '#3A3A3A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = r() > 0.5 ? '#2A2A2A' : '#4A4A4A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
  });

  // Tile 10: Snow top
  drawTile(10, (ctx, s, r) => {
    ctx.fillStyle = '#F0F0F0';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#E0E0E8' : '#FFFFFF';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 11: Snow grass side
  drawTile(11, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A5A30' : '#9C7C4E';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    ctx.fillStyle = '#F0F0F0';
    ctx.fillRect(0, 0, s, 3);
    ctx.fillStyle = '#E0E0E8';
    for (let x = 0; x < s; x++) {
      const h = r() > 0.5 ? 4 : 3;
      ctx.fillRect(x, 0, 1, h);
    }
  });

  // Tile 12: Cactus
  drawTile(12, (ctx, s, r) => {
    ctx.fillStyle = '#2A7A10';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#1A6A00' : '#3A8A20';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 2);
    }
    // Spines
    ctx.fillStyle = '#CCDD88';
    for (let i = 0; i < 6; i++) {
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 13: Sandstone
  drawTile(13, (ctx, s, r) => {
    ctx.fillStyle = '#C4A050';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = '#B09040';
      ctx.fillRect(0, y, s, 1);
    }
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#D4B060' : '#B49040';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 14: Planks
  drawTile(14, (ctx, s, r) => {
    ctx.fillStyle = '#B89050';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = '#A08040';
      ctx.fillRect(0, y, s, 1);
    }
    for (let x = 0; x < s; x += 4) {
      ctx.fillStyle = '#C8A060';
      ctx.fillRect(x, 0, 1, s);
    }
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#C8A060' : '#A08040';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 15: Cobblestone
  drawTile(15, (ctx, s, r) => {
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    // Draw stone-like pattern
    for (let y = 0; y < s; y += 4) {
      for (let x = 0; x < s; x += 4) {
        const offset = (y / 4) % 2 === 0 ? 0 : 2;
        const shade = r();
        ctx.fillStyle = shade > 0.6 ? '#8A8A8A' : shade > 0.3 ? '#6A6A6A' : '#5A5A5A';
        ctx.fillRect(x + offset, y, 3, 3);
      }
    }
    ctx.fillStyle = '#555555';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
    for (let x = 0; x < s; x += 4) {
      ctx.fillRect(x, 0, 1, s);
    }
  });

  // Tile 16: Glass
  drawTile(16, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(200, 230, 255, 0.4)';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = '#AACCEE';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, s - 1, s - 1);
    ctx.strokeRect(s / 2, 0, 0, s);
    ctx.beginPath();
    ctx.moveTo(0, s / 2);
    ctx.lineTo(s, s / 2);
    ctx.stroke();
    // Shine effect
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillRect(2, 2, 3, 3);
  });

  // Tile 17: Wheat
  drawTile(17, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    // Wheat stalks
    for (let x = 1; x < s; x += 3) {
      ctx.fillStyle = '#D4AA30';
      ctx.fillRect(x, 2, 1, s - 2);
      // Wheat head
      ctx.fillStyle = '#E8C840';
      ctx.fillRect(x - 1, 0, 3, 4);
    }
  });

  // Tile 18: Door (closed)
  drawTile(18, (ctx, s, r) => {
    // Wooden door background
    ctx.fillStyle = '#8B5E3C';
    ctx.fillRect(0, 0, s, s);
    // Vertical planks
    for (let x = 0; x < s; x += 4) {
      ctx.fillStyle = '#7A4E2C';
      ctx.fillRect(x, 0, 1, s);
    }
    // Horizontal bands
    ctx.fillStyle = '#6A3E1C';
    ctx.fillRect(0, 0, s, 2);
    ctx.fillRect(0, s - 2, s, 2);
    ctx.fillRect(0, Math.floor(s / 2) - 1, s, 2);
    // Handle
    ctx.fillStyle = '#444444';
    ctx.fillRect(s - 4, Math.floor(s / 2) - 1, 2, 3);
    // Border
    ctx.strokeStyle = '#5A3010';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, s - 1, s - 1);
  });

  // Tile 19: Door (open) - translucent, mostly empty
  drawTile(19, (ctx, s, r) => {
    // Thin door edge visible (door swung open)
    ctx.fillStyle = 'rgba(139, 94, 60, 0.3)';
    ctx.fillRect(0, 0, s, s);
    // Door edge on left side
    ctx.fillStyle = 'rgba(139, 94, 60, 0.7)';
    ctx.fillRect(0, 0, 3, s);
    ctx.fillStyle = 'rgba(90, 48, 16, 0.7)';
    ctx.fillRect(0, 0, 1, s);
    // Hinge marks
    ctx.fillStyle = 'rgba(80, 80, 80, 0.5)';
    ctx.fillRect(0, 2, 2, 2);
    ctx.fillRect(0, s - 4, 2, 2);
  });

  // =============================================
  // NEW TILES 20+
  // =============================================

  // Tile 20: Coal Ore - stone with black specks
  drawTile(20, (ctx, s, r) => {
    drawOre(ctx, s, r, '#222222', '#111111');
  });

  // Tile 21: Iron Ore - stone with tan/brown specks
  drawTile(21, (ctx, s, r) => {
    drawOre(ctx, s, r, '#C4A06A', '#B08850');
  });

  // Tile 22: Gold Ore - stone with yellow specks
  drawTile(22, (ctx, s, r) => {
    drawOre(ctx, s, r, '#E8D44A', '#D4C030');
  });

  // Tile 23: Diamond Ore - stone with cyan/blue specks
  drawTile(23, (ctx, s, r) => {
    drawOre(ctx, s, r, '#5AE8D4', '#40D0C0');
  });

  // Tile 24: Redstone Ore - stone with red specks
  drawTile(24, (ctx, s, r) => {
    drawOre(ctx, s, r, '#DD2222', '#BB1111');
  });

  // Tile 25: Lapis Ore - stone with blue specks
  drawTile(25, (ctx, s, r) => {
    drawOre(ctx, s, r, '#2244AA', '#1A3388');
  });

  // Tile 26: Emerald Ore - stone with green specks
  drawTile(26, (ctx, s, r) => {
    drawOre(ctx, s, r, '#22CC44', '#18AA38');
  });

  // Tile 27: Obsidian - very dark purple/black
  drawTile(27, (ctx, s, r) => {
    ctx.fillStyle = '#1A0A2E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 50; i++) {
      const v = r();
      ctx.fillStyle = v > 0.7 ? '#2A1A3E' : v > 0.4 ? '#100820' : '#1E0E32';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), r() > 0.5 ? 2 : 1, r() > 0.5 ? 2 : 1);
    }
    // Slight purple sheen highlights
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = 'rgba(80, 40, 120, 0.3)';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 28: Torch - brown stick with orange flame top
  drawTile(28, (ctx, s, r) => {
    // Transparent background
    ctx.clearRect(0, 0, s, s);
    const cx = Math.floor(s / 2);
    // Stick
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(cx - 1, 5, 2, s - 5);
    ctx.fillStyle = '#7A5A30';
    ctx.fillRect(cx, 5, 1, s - 5);
    // Flame - orange/yellow
    ctx.fillStyle = '#FF8800';
    ctx.fillRect(cx - 2, 2, 4, 4);
    ctx.fillStyle = '#FFCC00';
    ctx.fillRect(cx - 1, 1, 2, 4);
    ctx.fillStyle = '#FFEE44';
    ctx.fillRect(cx - 1, 2, 2, 2);
    // Flame tip
    ctx.fillStyle = '#FFDD00';
    ctx.fillRect(cx, 0, 1, 2);
  });

  // Tile 29: Crafting Table top - grid pattern
  drawTile(29, (ctx, s, r) => {
    // Lighter wood base
    ctx.fillStyle = '#C49860';
    ctx.fillRect(0, 0, s, s);
    // Grid lines - dark cross pattern
    ctx.fillStyle = '#6A4020';
    ctx.fillRect(0, Math.floor(s / 2) - 1, s, 1);
    ctx.fillRect(Math.floor(s / 2) - 1, 0, 1, s);
    // Border
    ctx.fillStyle = '#8A6040';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    // Noise
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#D4A870' : '#B48850';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 30: Crafting Table side - tools pattern
  drawTile(30, (ctx, s, r) => {
    // Wood plank base
    ctx.fillStyle = '#B89050';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = '#A08040';
      ctx.fillRect(0, y, s, 1);
    }
    // Tool shapes on side: a saw-like pattern
    ctx.fillStyle = '#888888';
    // Small hammer shape
    ctx.fillRect(3, 3, 4, 2);
    ctx.fillRect(4, 5, 2, 5);
    // Small pickaxe shape
    ctx.fillRect(9, 3, 4, 2);
    ctx.fillRect(10, 5, 2, 5);
    ctx.fillRect(9, 3, 1, 1);
    ctx.fillRect(13, 3, 1, 1);
    // Darker border
    ctx.fillStyle = '#7A5030';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
  });

  // Tile 31: Furnace top - stone-like
  drawTile(31, (ctx, s, r) => {
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      const shade = r();
      ctx.fillStyle = shade > 0.5 ? '#6A6A6A' : '#8A8A8A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Darker border
    ctx.fillStyle = '#5A5A5A';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
  });

  // Tile 32: Furnace front - stone with dark opening
  drawTile(32, (ctx, s, r) => {
    // Stone background
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#6A6A6A' : '#8A8A8A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Furnace opening (dark rectangle)
    ctx.fillStyle = '#222222';
    ctx.fillRect(4, 5, 8, 7);
    ctx.fillStyle = '#333333';
    ctx.fillRect(5, 6, 6, 5);
    // Opening border
    ctx.fillStyle = '#555555';
    ctx.fillRect(4, 5, 8, 1);
    ctx.fillRect(4, 12, 8, 1);
    ctx.fillRect(4, 5, 1, 8);
    ctx.fillRect(11, 5, 1, 8);
    // Grid at bottom of opening
    ctx.fillStyle = '#444444';
    ctx.fillRect(5, 10, 6, 1);
  });

  // Tile 33: Furnace side - plain stone
  drawTile(33, (ctx, s, r) => {
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 35; i++) {
      ctx.fillStyle = r() > 0.5 ? '#6A6A6A' : '#8A8A8A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Subtle border
    ctx.fillStyle = '#5A5A5A';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
  });

  // Tile 34: Furnace lit front - stone with orange glowing opening
  drawTile(34, (ctx, s, r) => {
    // Stone background
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#6A6A6A' : '#8A8A8A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Furnace opening with orange glow
    ctx.fillStyle = '#DD6600';
    ctx.fillRect(4, 5, 8, 7);
    ctx.fillStyle = '#FF8800';
    ctx.fillRect(5, 6, 6, 5);
    ctx.fillStyle = '#FFAA22';
    ctx.fillRect(6, 7, 4, 3);
    // Fire flicker
    ctx.fillStyle = '#FFCC44';
    ctx.fillRect(6, 7, 2, 2);
    ctx.fillRect(8, 8, 2, 1);
    // Opening border
    ctx.fillStyle = '#555555';
    ctx.fillRect(4, 5, 8, 1);
    ctx.fillRect(4, 12, 8, 1);
    ctx.fillRect(4, 5, 1, 8);
    ctx.fillRect(11, 5, 1, 8);
    // Grid at bottom
    ctx.fillStyle = '#AA4400';
    ctx.fillRect(5, 10, 6, 1);
  });

  // Tile 35: Chest top - dark wood with latch
  drawTile(35, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    // Wood grain
    for (let y = 0; y < s; y += 3) {
      ctx.fillStyle = '#7A5A30';
      ctx.fillRect(0, y, s, 1);
    }
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#9C7C4E' : '#7A5A30';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Metal latch in center
    ctx.fillStyle = '#555555';
    ctx.fillRect(Math.floor(s / 2) - 2, Math.floor(s / 2) - 1, 4, 2);
    ctx.fillStyle = '#777777';
    ctx.fillRect(Math.floor(s / 2) - 1, Math.floor(s / 2) - 1, 2, 2);
  });

  // Tile 36: Chest front - wood with latch
  drawTile(36, (ctx, s, r) => {
    ctx.fillStyle = '#9C7C4E';
    ctx.fillRect(0, 0, s, s);
    // Horizontal planks
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = '#8A6A3A';
      ctx.fillRect(0, y, s, 1);
    }
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#AC8C5E' : '#8A6A3A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Metal latch/lock
    ctx.fillStyle = '#555555';
    ctx.fillRect(Math.floor(s / 2) - 2, 5, 4, 3);
    ctx.fillStyle = '#777777';
    ctx.fillRect(Math.floor(s / 2) - 1, 5, 2, 2);
    // Border trim
    ctx.fillStyle = '#6A4A2A';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
  });

  // Tile 37: Chest side - plain wood
  drawTile(37, (ctx, s, r) => {
    ctx.fillStyle = '#9C7C4E';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = '#8A6A3A';
      ctx.fillRect(0, y, s, 1);
    }
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#AC8C5E' : '#8A6A3A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Border
    ctx.fillStyle = '#6A4A2A';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
  });

  // Tile 38: TNT top - red with crossing lines
  drawTile(38, (ctx, s, r) => {
    ctx.fillStyle = '#CC2200';
    ctx.fillRect(0, 0, s, s);
    // Cross pattern
    ctx.fillStyle = '#AA1800';
    ctx.fillRect(0, Math.floor(s / 2) - 1, s, 2);
    ctx.fillRect(Math.floor(s / 2) - 1, 0, 2, s);
    // Fuse in center
    ctx.fillStyle = '#444444';
    ctx.fillRect(Math.floor(s / 2) - 1, Math.floor(s / 2) - 1, 2, 2);
    ctx.fillStyle = '#888888';
    ctx.fillRect(Math.floor(s / 2), Math.floor(s / 2), 1, 1);
    // Noise
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#DD3300' : '#BB2000';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 39: TNT side - red with "TNT" lettering pattern
  drawTile(39, (ctx, s, r) => {
    ctx.fillStyle = '#CC2200';
    ctx.fillRect(0, 0, s, s);
    // White band in the middle for label
    ctx.fillStyle = '#EEEEEE';
    ctx.fillRect(0, 4, s, 8);
    // "TNT" letters (pixel art in 3 wide sections)
    ctx.fillStyle = '#CC2200';
    // T (cols 1-4)
    ctx.fillRect(1, 5, 3, 1);  // top bar
    ctx.fillRect(2, 5, 1, 6);  // vertical
    // N (cols 5-8)
    ctx.fillRect(5, 5, 1, 6);  // left vertical
    ctx.fillRect(6, 6, 1, 1);
    ctx.fillRect(7, 7, 1, 1);
    ctx.fillRect(8, 5, 1, 6);  // right vertical
    // T (cols 10-13)
    ctx.fillRect(10, 5, 3, 1); // top bar
    ctx.fillRect(11, 5, 1, 6); // vertical
    // Dark bands top/bottom
    ctx.fillStyle = '#AA1800';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 3, s, 1);
    ctx.fillRect(0, 12, s, 1);
  });

  // Tile 40: TNT bottom - tan/beige
  drawTile(40, (ctx, s, r) => {
    ctx.fillStyle = '#C4A050';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#B49040' : '#D4B060';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 41: Bookshelf side - books between planks
  drawTile(41, (ctx, s, r) => {
    // Plank background
    ctx.fillStyle = '#B89050';
    ctx.fillRect(0, 0, s, s);
    // Top and bottom plank borders
    ctx.fillStyle = '#A08040';
    ctx.fillRect(0, 0, s, 2);
    ctx.fillRect(0, s - 2, s, 2);
    // Books area
    const bookColors = ['#CC2222', '#2244AA', '#228822', '#8B4513', '#886622', '#664488', '#AA4444'];
    let bx = 0;
    let row = 0;
    for (row = 0; row < 2; row++) {
      bx = 0;
      const by = row === 0 ? 2 : Math.floor(s / 2);
      const bh = Math.floor(s / 2) - 2;
      while (bx < s) {
        const bw = Math.floor(r() * 2) + 2;
        ctx.fillStyle = bookColors[Math.floor(r() * bookColors.length)];
        ctx.fillRect(bx, by, bw, bh);
        // Book spine line
        ctx.fillStyle = 'rgba(0,0,0,0.2)';
        ctx.fillRect(bx, by, 1, bh);
        // Book top highlight
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.fillRect(bx, by, bw, 1);
        bx += bw + 1;
      }
    }
    // Plank shelf divider
    ctx.fillStyle = '#A08040';
    ctx.fillRect(0, Math.floor(s / 2) - 1, s, 2);
  });

  // Tile 42: Bricks - red brick pattern
  drawTile(42, (ctx, s, r) => {
    ctx.fillStyle = '#AA5533';
    ctx.fillRect(0, 0, s, s);
    // Mortar lines
    ctx.fillStyle = '#BBAA99';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
    // Vertical mortar (offset every other row)
    for (let y = 0; y < s; y += 4) {
      const off = ((y / 4) % 2 === 0) ? 0 : 4;
      for (let x = off; x < s; x += 8) {
        ctx.fillRect(x, y, 1, 4);
      }
    }
    // Brick color variation
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#BB6644' : '#995522';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 43: Iron Block - shiny light gray
  drawTile(43, (ctx, s, r) => {
    ctx.fillStyle = '#D8D8D8';
    ctx.fillRect(0, 0, s, s);
    // Horizontal bands for metallic look
    for (let y = 0; y < s; y += 3) {
      ctx.fillStyle = y % 6 === 0 ? '#C8C8C8' : '#E4E4E4';
      ctx.fillRect(1, y, s - 2, 2);
    }
    // Edge border
    ctx.fillStyle = '#AAAAAA';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    // Shine
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.fillRect(2, 2, 3, 2);
    // Cross indent
    ctx.fillStyle = '#BBBBBB';
    ctx.fillRect(3, 3, s - 6, 1);
    ctx.fillRect(3, s - 4, s - 6, 1);
    ctx.fillRect(3, 3, 1, s - 6);
    ctx.fillRect(s - 4, 3, 1, s - 6);
  });

  // Tile 44: Gold Block - shiny gold
  drawTile(44, (ctx, s, r) => {
    ctx.fillStyle = '#E8C840';
    ctx.fillRect(0, 0, s, s);
    // Horizontal bands
    for (let y = 0; y < s; y += 3) {
      ctx.fillStyle = y % 6 === 0 ? '#D4B430' : '#F0D850';
      ctx.fillRect(1, y, s - 2, 2);
    }
    // Edge border
    ctx.fillStyle = '#C4A020';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    // Shine
    ctx.fillStyle = 'rgba(255,255,200,0.4)';
    ctx.fillRect(2, 2, 3, 2);
    // Cross indent
    ctx.fillStyle = '#C0A020';
    ctx.fillRect(3, 3, s - 6, 1);
    ctx.fillRect(3, s - 4, s - 6, 1);
    ctx.fillRect(3, 3, 1, s - 6);
    ctx.fillRect(s - 4, 3, 1, s - 6);
  });

  // Tile 45: Diamond Block - shiny cyan
  drawTile(45, (ctx, s, r) => {
    ctx.fillStyle = '#5AE8D4';
    ctx.fillRect(0, 0, s, s);
    // Horizontal bands
    for (let y = 0; y < s; y += 3) {
      ctx.fillStyle = y % 6 === 0 ? '#48D4C0' : '#6CF0E0';
      ctx.fillRect(1, y, s - 2, 2);
    }
    // Edge border
    ctx.fillStyle = '#38C0B0';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    // Shine
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.fillRect(2, 2, 3, 2);
    // Cross indent
    ctx.fillStyle = '#40C8B8';
    ctx.fillRect(3, 3, s - 6, 1);
    ctx.fillRect(3, s - 4, s - 6, 1);
    ctx.fillRect(3, 3, 1, s - 6);
    ctx.fillRect(s - 4, 3, 1, s - 6);
  });

  // Tile 46: Fence - wood fence post
  drawTile(46, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Central post
    ctx.fillStyle = '#B89050';
    ctx.fillRect(5, 0, 6, s);
    // Wood grain
    ctx.fillStyle = '#A08040';
    ctx.fillRect(5, 0, 6, 1);
    ctx.fillRect(5, 4, 6, 1);
    ctx.fillRect(5, 8, 6, 1);
    ctx.fillRect(5, 12, 6, 1);
    // Horizontal rails
    ctx.fillStyle = '#C8A060';
    ctx.fillRect(0, 3, s, 2);
    ctx.fillRect(0, 10, s, 2);
    // Rail dark edges
    ctx.fillStyle = '#907030';
    ctx.fillRect(0, 3, s, 1);
    ctx.fillRect(0, 10, s, 1);
  });

  // Tile 47: Ladder - wooden ladder rungs
  drawTile(47, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Side rails
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(1, 0, 2, s);
    ctx.fillRect(s - 3, 0, 2, s);
    // Rungs
    ctx.fillStyle = '#A08040';
    for (let y = 2; y < s; y += 4) {
      ctx.fillRect(1, y, s - 2, 2);
    }
    // Rail highlights
    ctx.fillStyle = '#9C7C4E';
    ctx.fillRect(2, 0, 1, s);
    ctx.fillRect(s - 2, 0, 1, s);
  });

  // Tile 48: Red Flower - red flower on transparent
  drawTile(48, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Stem
    ctx.fillStyle = '#2A7A10';
    ctx.fillRect(7, 6, 2, s - 6);
    // Leaf
    ctx.fillStyle = '#3A8A20';
    ctx.fillRect(5, 10, 3, 2);
    ctx.fillRect(10, 9, 3, 2);
    // Petals - red
    ctx.fillStyle = '#DD2222';
    ctx.fillRect(5, 2, 2, 3);
    ctx.fillRect(9, 2, 2, 3);
    ctx.fillRect(6, 1, 4, 2);
    ctx.fillRect(6, 5, 4, 2);
    // Center
    ctx.fillStyle = '#FFCC00';
    ctx.fillRect(7, 3, 2, 2);
  });

  // Tile 49: Yellow Flower - yellow flower on transparent
  drawTile(49, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Stem
    ctx.fillStyle = '#2A7A10';
    ctx.fillRect(7, 6, 2, s - 6);
    // Leaf
    ctx.fillStyle = '#3A8A20';
    ctx.fillRect(4, 10, 3, 2);
    // Petals - yellow
    ctx.fillStyle = '#FFDD00';
    ctx.fillRect(5, 2, 2, 3);
    ctx.fillRect(9, 2, 2, 3);
    ctx.fillRect(6, 1, 4, 2);
    ctx.fillRect(6, 5, 4, 2);
    // Center
    ctx.fillStyle = '#AA6600';
    ctx.fillRect(7, 3, 2, 2);
  });

  // Tile 50: Tall Grass - green grass strands on transparent
  drawTile(50, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Grass blades of varying height
    for (let x = 0; x < s; x += 2) {
      const h = Math.floor(r() * 8) + 6;
      const green = r() > 0.5 ? '#3A8A20' : '#4A9A30';
      ctx.fillStyle = green;
      ctx.fillRect(x, s - h, 1, h);
      if (r() > 0.3) {
        ctx.fillRect(x + 1, s - h + 2, 1, h - 2);
      }
    }
    // Tips
    for (let x = 1; x < s; x += 3) {
      ctx.fillStyle = '#5BAA40';
      ctx.fillRect(x, s - Math.floor(r() * 4) - 10, 1, 2);
    }
  });

  // Tile 51: Red Mushroom - red cap with white dots
  drawTile(51, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Stem
    ctx.fillStyle = '#E8E0D0';
    ctx.fillRect(6, 8, 4, s - 8);
    ctx.fillStyle = '#D0C8B8';
    ctx.fillRect(6, 8, 1, s - 8);
    // Cap - red dome
    ctx.fillStyle = '#DD2222';
    ctx.fillRect(3, 4, 10, 5);
    ctx.fillRect(4, 3, 8, 1);
    ctx.fillRect(5, 2, 6, 1);
    // White dots
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(5, 3, 2, 2);
    ctx.fillRect(9, 3, 2, 2);
    ctx.fillRect(7, 5, 2, 2);
    ctx.fillRect(4, 6, 1, 1);
    ctx.fillRect(11, 6, 1, 1);
  });

  // Tile 52: Brown Mushroom
  drawTile(52, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Stem
    ctx.fillStyle = '#E8E0D0';
    ctx.fillRect(6, 8, 4, s - 8);
    ctx.fillStyle = '#D0C8B8';
    ctx.fillRect(6, 8, 1, s - 8);
    // Cap - brown dome (flatter)
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(3, 5, 10, 4);
    ctx.fillRect(5, 4, 6, 1);
    // Cap variation
    ctx.fillStyle = '#7A5A30';
    ctx.fillRect(4, 5, 2, 2);
    ctx.fillRect(10, 5, 2, 2);
    ctx.fillStyle = '#9C7C4E';
    ctx.fillRect(6, 5, 4, 2);
  });

  // Tile 53: Gravel - gray gravel texture
  drawTile(53, (ctx, s, r) => {
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 60; i++) {
      const v = r();
      ctx.fillStyle = v > 0.7 ? '#999999' : v > 0.4 ? '#707070' : '#606060';
      const size = r() > 0.5 ? 2 : 1;
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), size, size);
    }
    // Some darker specks
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = '#505050';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 54: Clay - light gray-brown
  drawTile(54, (ctx, s, r) => {
    ctx.fillStyle = '#A0A0AA';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#9090A0' : '#B0B0B8';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Subtle cracks
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = '#888898';
      const sx = Math.floor(r() * s);
      const sy = Math.floor(r() * s);
      ctx.fillRect(sx, sy, 3, 1);
    }
  });

  // Tile 55: Birch log bark - white/light bark with dark horizontal lines
  drawTile(55, (ctx, s, r) => {
    ctx.fillStyle = '#E8E0D0';
    ctx.fillRect(0, 0, s, s);
    // Dark horizontal birch marks
    for (let y = 0; y < s; y += 3) {
      if (r() > 0.4) {
        ctx.fillStyle = '#2A2A2A';
        const markW = Math.floor(r() * 6) + 2;
        const markX = Math.floor(r() * (s - markW));
        ctx.fillRect(markX, y, markW, 1);
      }
    }
    // Slight color variation
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#D8D0C0' : '#F0E8D8';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 56: Birch log top - rings on lighter wood
  drawTile(56, (ctx, s, r) => {
    ctx.fillStyle = '#D8D0B8';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = '#C0B098';
    ctx.lineWidth = 1;
    const cx = s / 2, cy = s / 2;
    for (let rad = 2; rad < s / 2; rad += 2) {
      ctx.beginPath();
      ctx.arc(cx, cy, rad, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.fillStyle = '#8A7A60';
    ctx.fillRect(cx - 1, cy - 1, 2, 2);
    // Outer bark ring
    ctx.fillStyle = '#E8E0D0';
    ctx.strokeStyle = '#D8D0C0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, s / 2 - 1, 0, Math.PI * 2);
    ctx.stroke();
  });

  // Tile 57: Birch Leaves - lighter green
  drawTile(57, (ctx, s, r) => {
    ctx.fillStyle = '#5AAA30';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#4A9A20' : '#6ABB40';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Some yellow-green
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = '#7ACC50';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 58: Snow block - pure white
  drawTile(58, (ctx, s, r) => {
    ctx.fillStyle = '#F4F4F8';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#EAEAF0' : '#FFFFFF';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Subtle shadow spots
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = '#E0E0E8';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
  });

  // Tile 59: Ice - translucent blue
  drawTile(59, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(140, 200, 255, 0.7)';
    ctx.fillRect(0, 0, s, s);
    // Ice cracks
    ctx.fillStyle = 'rgba(200, 230, 255, 0.6)';
    for (let i = 0; i < 6; i++) {
      const sx = Math.floor(r() * s);
      const sy = Math.floor(r() * s);
      ctx.fillRect(sx, sy, Math.floor(r() * 5) + 1, 1);
      ctx.fillRect(sx, sy, 1, Math.floor(r() * 4) + 1);
    }
    // Shine
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fillRect(2, 2, 3, 2);
    ctx.fillRect(1, 3, 2, 1);
    // Darker spots
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = 'rgba(100, 160, 220, 0.4)';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
  });

  // Tile 60: Pumpkin top - green stem circle
  drawTile(60, (ctx, s, r) => {
    ctx.fillStyle = '#CC8820';
    ctx.fillRect(0, 0, s, s);
    // Slight orange variation
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#BB7710' : '#DD9930';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Segment lines (like a pumpkin top view)
    ctx.fillStyle = '#AA6610';
    ctx.fillRect(0, Math.floor(s / 2), s, 1);
    ctx.fillRect(Math.floor(s / 2), 0, 1, s);
    // Green stem in center
    ctx.fillStyle = '#3A7A10';
    ctx.fillRect(6, 6, 4, 4);
    ctx.fillStyle = '#2A6A00';
    ctx.fillRect(7, 7, 2, 2);
  });

  // Tile 61: Pumpkin side - orange with face creases
  drawTile(61, (ctx, s, r) => {
    ctx.fillStyle = '#DD8820';
    ctx.fillRect(0, 0, s, s);
    // Vertical segment lines
    ctx.fillStyle = '#BB6610';
    ctx.fillRect(4, 0, 1, s);
    ctx.fillRect(11, 0, 1, s);
    // Lighter center strips
    ctx.fillStyle = '#EE9930';
    ctx.fillRect(5, 0, 3, s);
    ctx.fillRect(8, 0, 3, s);
    // Top and bottom darker edges
    ctx.fillStyle = '#AA5510';
    ctx.fillRect(0, 0, s, 2);
    ctx.fillRect(0, s - 2, s, 2);
    // Noise
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#CC7718' : '#EE9928';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 62: Melon top - green
  drawTile(62, (ctx, s, r) => {
    ctx.fillStyle = '#4A8A20';
    ctx.fillRect(0, 0, s, s);
    // Cross pattern
    ctx.fillStyle = '#3A7A10';
    ctx.fillRect(0, Math.floor(s / 2), s, 1);
    ctx.fillRect(Math.floor(s / 2), 0, 1, s);
    // Center
    ctx.fillStyle = '#5A9A30';
    ctx.fillRect(6, 6, 4, 4);
    // Noise
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#3A7A15' : '#5A9A28';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 63: Melon side - green stripes on lighter green
  drawTile(63, (ctx, s, r) => {
    ctx.fillStyle = '#8AAA30';
    ctx.fillRect(0, 0, s, s);
    // Vertical dark green stripes
    for (let x = 0; x < s; x += 4) {
      ctx.fillStyle = '#4A7A10';
      ctx.fillRect(x, 0, 2, s);
    }
    // Lighter highlights between stripes
    for (let x = 2; x < s; x += 4) {
      ctx.fillStyle = '#9ABB40';
      ctx.fillRect(x, 0, 1, s);
    }
    // Top/bottom edges darker
    ctx.fillStyle = '#3A6A08';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    // Noise
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A9A28' : '#5A8A18';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 64: Wool White - fluffy white texture
  drawTile(64, (ctx, s, r) => {
    ctx.fillStyle = '#F0F0F0';
    ctx.fillRect(0, 0, s, s);
    // Fluffy bumps
    for (let i = 0; i < 50; i++) {
      const v = r();
      ctx.fillStyle = v > 0.6 ? '#FFFFFF' : v > 0.3 ? '#E8E8E8' : '#E0E0E0';
      const size = Math.floor(r() * 3) + 1;
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), size, size);
    }
  });

  // Tile 65: Moss Stone - cobblestone with green moss
  drawTile(65, (ctx, s, r) => {
    // Start with cobblestone base
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      for (let x = 0; x < s; x += 4) {
        const offset = (y / 4) % 2 === 0 ? 0 : 2;
        const shade = r();
        ctx.fillStyle = shade > 0.6 ? '#8A8A8A' : shade > 0.3 ? '#6A6A6A' : '#5A5A5A';
        ctx.fillRect(x + offset, y, 3, 3);
      }
    }
    ctx.fillStyle = '#555555';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
    for (let x = 0; x < s; x += 4) {
      ctx.fillRect(x, 0, 1, s);
    }
    // Green moss patches
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#3A7A1A' : '#2A6A10';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = '#4A8A2A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 66: Netherrack - red/dark red rocky
  drawTile(66, (ctx, s, r) => {
    ctx.fillStyle = '#6A2020';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 50; i++) {
      const v = r();
      ctx.fillStyle = v > 0.6 ? '#7A2828' : v > 0.3 ? '#5A1818' : '#4A1010';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), r() > 0.5 ? 2 : 1, r() > 0.5 ? 2 : 1);
    }
    // Dark cracks
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = '#3A0808';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), Math.floor(r() * 4) + 1, 1);
    }
  });

  // Tile 67: Glowstone - yellow glowing
  drawTile(67, (ctx, s, r) => {
    ctx.fillStyle = '#D4AA40';
    ctx.fillRect(0, 0, s, s);
    // Bright spots
    for (let i = 0; i < 30; i++) {
      const v = r();
      ctx.fillStyle = v > 0.6 ? '#EEDD60' : v > 0.3 ? '#C4992C' : '#E8CC50';
      const size = Math.floor(r() * 3) + 1;
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), size, size);
    }
    // Bright glow centers
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = '#FFEE88';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Dark crack lines
    ctx.fillStyle = '#AA8820';
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), Math.floor(r() * 5) + 1, 1);
    }
  });

  // Tile 68: Soul Sand - dark brown with face-like dark spots
  drawTile(68, (ctx, s, r) => {
    ctx.fillStyle = '#5A4830';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#4A3820' : '#6A5840';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Face-like dark spots (eyes and mouth)
    ctx.fillStyle = '#2A1808';
    // "Eyes"
    ctx.fillRect(3, 4, 3, 2);
    ctx.fillRect(10, 4, 3, 2);
    // "Mouth"
    ctx.fillRect(4, 10, 8, 2);
    ctx.fillRect(5, 12, 6, 1);
    // Additional random dark spots
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = '#3A2810';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
  });

  // Tile 69: Nether Bricks - dark purple-red bricks
  drawTile(69, (ctx, s, r) => {
    ctx.fillStyle = '#3A1A2A';
    ctx.fillRect(0, 0, s, s);
    // Mortar
    ctx.fillStyle = '#2A0A1A';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
    for (let y = 0; y < s; y += 4) {
      const off = ((y / 4) % 2 === 0) ? 0 : 4;
      for (let x = off; x < s; x += 8) {
        ctx.fillRect(x, y, 1, 4);
      }
    }
    // Brick variation
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#4A2A3A' : '#301020';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 70: End Stone - pale yellow with spots
  drawTile(70, (ctx, s, r) => {
    ctx.fillStyle = '#DDDDA8';
    ctx.fillRect(0, 0, s, s);
    // Spots and texture
    for (let i = 0; i < 40; i++) {
      const v = r();
      ctx.fillStyle = v > 0.6 ? '#CCCC98' : v > 0.3 ? '#EEEEB8' : '#C4C490';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Small dark crater spots
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = '#BBBB80';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // =============================================
  // CRACK OVERLAY TILES 71-74 (4 etapas de grietas)
  // Semi-transparentes, se superponen sobre la cara del bloque
  // =============================================

  // Tile 71: Grieta etapa 1 - grietas leves
  drawTile(71, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.25)';
    ctx.lineWidth = 1;
    // Unas pocas líneas finas
    ctx.beginPath();
    ctx.moveTo(4, 2);
    ctx.lineTo(7, 6);
    ctx.lineTo(5, 10);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(11, 5);
    ctx.lineTo(9, 9);
    ctx.stroke();
  });

  // Tile 72: Grieta etapa 2 - grietas moderadas
  drawTile(72, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.lineWidth = 1;
    // Más líneas, más ramificadas
    ctx.beginPath();
    ctx.moveTo(3, 1);
    ctx.lineTo(6, 5);
    ctx.lineTo(4, 9);
    ctx.lineTo(6, 13);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(10, 2);
    ctx.lineTo(8, 7);
    ctx.lineTo(11, 11);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(6, 5);
    ctx.lineTo(10, 6);
    ctx.stroke();
    // Algunas áreas oscuras
    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
    ctx.fillRect(5, 4, 2, 2);
    ctx.fillRect(9, 8, 2, 2);
  });

  // Tile 73: Grieta etapa 3 - grietas severas
  drawTile(73, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.lineWidth = 1;
    // Red de grietas densa
    ctx.beginPath();
    ctx.moveTo(2, 0);
    ctx.lineTo(5, 4);
    ctx.lineTo(3, 8);
    ctx.lineTo(5, 12);
    ctx.lineTo(3, 15);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(10, 0);
    ctx.lineTo(8, 5);
    ctx.lineTo(11, 9);
    ctx.lineTo(9, 14);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(5, 4);
    ctx.lineTo(8, 5);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(3, 8);
    ctx.lineTo(7, 7);
    ctx.lineTo(11, 9);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(13, 3);
    ctx.lineTo(11, 7);
    ctx.lineTo(14, 11);
    ctx.stroke();
    // Áreas oscuras de impacto
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.fillRect(4, 3, 3, 3);
    ctx.fillRect(8, 7, 3, 3);
    ctx.fillRect(2, 10, 2, 2);
  });

  // Tile 74: Grieta etapa 4 - a punto de romperse
  drawTile(74, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Fondo semi-oscuro general
    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.lineWidth = 1;
    // Grietas muy densas por todo el tile
    ctx.beginPath();
    ctx.moveTo(1, 0);
    ctx.lineTo(4, 4);
    ctx.lineTo(2, 8);
    ctx.lineTo(4, 12);
    ctx.lineTo(2, 15);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(7, 0);
    ctx.lineTo(5, 4);
    ctx.lineTo(8, 8);
    ctx.lineTo(6, 12);
    ctx.lineTo(8, 15);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(12, 0);
    ctx.lineTo(10, 4);
    ctx.lineTo(13, 8);
    ctx.lineTo(11, 12);
    ctx.lineTo(13, 15);
    ctx.stroke();
    // Grietas horizontales
    ctx.beginPath();
    ctx.moveTo(0, 4);
    ctx.lineTo(15, 5);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, 9);
    ctx.lineTo(15, 8);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, 13);
    ctx.lineTo(15, 12);
    ctx.stroke();
    // Grandes zonas oscuras de fragmentación
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(3, 2, 4, 3);
    ctx.fillRect(9, 6, 4, 3);
    ctx.fillRect(1, 10, 4, 3);
    ctx.fillRect(10, 11, 3, 3);
  });

  // Tile 75: Bed top (red blanket with pillow)
  drawTile(75, (ctx, s, r) => {
    ctx.fillStyle = '#AA2222';
    ctx.fillRect(0, 0, s, s);
    // Blanket texture
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#991818' : '#BB2828';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // White pillow at top
    ctx.fillStyle = '#E8E8E8';
    ctx.fillRect(3, 1, 10, 5);
    ctx.fillStyle = '#D0D0D0';
    ctx.fillRect(4, 2, 8, 3);
  });

  // Tile 76: Bed side (dark red with wood frame)
  drawTile(76, (ctx, s, r) => {
    // Wood frame at bottom
    ctx.fillStyle = '#8B5E3C';
    ctx.fillRect(0, s - 3, s, 3);
    // Red side
    ctx.fillStyle = '#AA2222';
    ctx.fillRect(0, 0, s, s - 3);
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#991818' : '#BB2828';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * (s - 3)), 1, 1);
    }
  });

  // Tile 77: Farmland dry — brown with horizontal furrow lines
  drawTile(77, (ctx, s, r) => {
    // Dirt base
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A5A30' : '#9C7C4E';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Horizontal furrow lines
    ctx.fillStyle = '#6A4A28';
    for (let y = 2; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
    // Lighter ridges between furrows
    ctx.fillStyle = '#9C7848';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
    // Some noise
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A5830' : '#8B6838';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 78: Farmland wet — darker brown with horizontal furrow lines
  drawTile(78, (ctx, s, r) => {
    // Dark wet dirt base
    ctx.fillStyle = '#5A4020';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#4A3015' : '#6A5030';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Horizontal furrow lines (darker)
    ctx.fillStyle = '#3A2810';
    for (let y = 2; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
    // Lighter ridges between furrows
    ctx.fillStyle = '#6A4828';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
    // Moisture sheen
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = 'rgba(100, 140, 180, 0.15)';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // =============================================
  // BIOME TILES 79-84 (Jungle, Savanna, Swamp)
  // =============================================

  // Tile 79: Jungle log bark - dark brown with green moss patches
  drawTile(79, (ctx, s, r) => {
    ctx.fillStyle = '#4A3018';
    ctx.fillRect(0, 0, s, s);
    // Bark lines
    for (let y = 0; y < s; y += 2) {
      ctx.fillStyle = y % 4 === 0 ? '#3A2010' : '#5A4028';
      ctx.fillRect(0, y, s, 1);
    }
    // Bark variation
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#3A2510' : '#5A3F28';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 2);
    }
    // Green moss patches
    for (let i = 0; i < 12; i++) {
      ctx.fillStyle = r() > 0.5 ? '#3A6A1A' : '#2A5A10';
      const mx = Math.floor(r() * s);
      const my = Math.floor(r() * s);
      ctx.fillRect(mx, my, 2, 2);
    }
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = '#4A7A2A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 80: Jungle leaves - dense dark green with tiny dots
  drawTile(80, (ctx, s, r) => {
    ctx.fillStyle = '#2A5A10';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 50; i++) {
      const v = r();
      ctx.fillStyle = v > 0.6 ? '#1A4A08' : v > 0.3 ? '#3A6A1A' : '#205A0A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Tiny lighter dots (light filtering through)
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = '#4A8A30';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 81: Acacia log bark - gray-tan with horizontal lines
  drawTile(81, (ctx, s, r) => {
    ctx.fillStyle = '#8A7A60';
    ctx.fillRect(0, 0, s, s);
    // Horizontal bark lines
    for (let y = 0; y < s; y += 2) {
      ctx.fillStyle = y % 4 === 0 ? '#7A6A50' : '#9A8A70';
      ctx.fillRect(0, y, s, 1);
    }
    // Bark texture variation
    for (let i = 0; i < 25; i++) {
      ctx.fillStyle = r() > 0.5 ? '#6A5A40' : '#AA9A80';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), r() > 0.5 ? 2 : 1, 1);
    }
    // Subtle darker cracks
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = '#5A4A30';
      const sx = Math.floor(r() * s);
      const sy = Math.floor(r() * s);
      ctx.fillRect(sx, sy, Math.floor(r() * 3) + 1, 1);
    }
  });

  // Tile 82: Acacia leaves - orange-red with green tints
  drawTile(82, (ctx, s, r) => {
    ctx.fillStyle = '#CC6A20';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      const v = r();
      ctx.fillStyle = v > 0.6 ? '#DD7A30' : v > 0.3 ? '#BB5A10' : '#AA5008';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Green tints scattered
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A9A30' : '#6A8A20';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Some deeper red spots
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = '#A04008';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 83: Vine - green vine pattern on transparent background
  drawTile(83, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Main vine strands
    for (let x = 1; x < s; x += 3) {
      const h = Math.floor(r() * 6) + 8;
      ctx.fillStyle = r() > 0.5 ? '#2A6A10' : '#3A7A1A';
      ctx.fillRect(x, s - h, 1, h);
      // Side leaves
      if (r() > 0.3) {
        ctx.fillStyle = '#3A8A20';
        ctx.fillRect(x - 1, s - h + 2, 1, 2);
        ctx.fillRect(x + 1, s - h + 4, 1, 2);
      }
    }
    // Cross-connections
    for (let y = 2; y < s; y += 4) {
      const vr = r();
      if (vr > 0.4) {
        ctx.fillStyle = '#2A7A12';
        const sx = Math.floor(r() * (s - 4));
        ctx.fillRect(sx, y, Math.floor(r() * 4) + 2, 1);
      }
    }
    // Leaf tips
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = '#4A9A30';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 84: Lily pad - green circular pad on transparent background
  drawTile(84, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    const cx = s / 2;
    const cy = s / 2;
    // Main pad shape (filled circle)
    ctx.fillStyle = '#2A7A10';
    ctx.beginPath();
    ctx.arc(cx, cy, s / 2 - 2, 0, Math.PI * 2);
    ctx.fill();
    // Lighter inner area
    ctx.fillStyle = '#3A8A20';
    ctx.beginPath();
    ctx.arc(cx, cy, s / 2 - 4, 0, Math.PI * 2);
    ctx.fill();
    // Notch cut-out (characteristic lily pad V)
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + s / 2, cy - 3);
    ctx.lineTo(cx + s / 2, cy + 3);
    ctx.closePath();
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    // Vein lines radiating from center
    ctx.strokeStyle = '#1A6A08';
    ctx.lineWidth = 1;
    for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angle) * (s / 2 - 3), cy + Math.sin(angle) * (s / 2 - 3));
      ctx.stroke();
    }
    // Center dot
    ctx.fillStyle = '#4A9A30';
    ctx.fillRect(Math.floor(cx) - 1, Math.floor(cy) - 1, 2, 2);
  });

  // Tile 85: Enchanting table top - dark obsidian base with a book/rune pattern
  drawTile(85, (ctx, s, r) => {
    // Dark obsidian base
    ctx.fillStyle = '#1A0A2E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#120822' : '#22103A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Diamond inlay border
    ctx.fillStyle = '#3A6A6A';
    ctx.fillRect(1, 1, s - 2, 1);
    ctx.fillRect(1, s - 2, s - 2, 1);
    ctx.fillRect(1, 1, 1, s - 2);
    ctx.fillRect(s - 2, 1, 1, s - 2);
    // Book shape in center
    ctx.fillStyle = '#8B2020';
    ctx.fillRect(4, 4, s - 8, s - 8);
    ctx.fillStyle = '#AA3030';
    ctx.fillRect(5, 5, s - 10, s - 10);
    // Book spine
    ctx.fillStyle = '#6A1818';
    ctx.fillRect(Math.floor(s / 2) - 1, 4, 2, s - 8);
    // Page lines
    ctx.fillStyle = '#D8D0C0';
    for (let y = 6; y < s - 6; y += 2) {
      ctx.fillRect(6, y, Math.floor(s / 2) - 8, 1);
      ctx.fillRect(Math.floor(s / 2) + 2, y, Math.floor(s / 2) - 8, 1);
    }
    // Mystical glow spots
    ctx.fillStyle = '#6040A0';
    ctx.fillRect(3, 3, 1, 1);
    ctx.fillRect(s - 4, 3, 1, 1);
    ctx.fillRect(3, s - 4, 1, 1);
    ctx.fillRect(s - 4, s - 4, 1, 1);
  });

  // Tile 86: Enchanting table side - dark obsidian with diamond trim
  drawTile(86, (ctx, s, r) => {
    // Dark obsidian base
    ctx.fillStyle = '#1A0A2E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#120822' : '#22103A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Top edge trim (lighter, represents table surface edge)
    ctx.fillStyle = '#3A2050';
    ctx.fillRect(0, 0, s, 2);
    // Diamond inlay stripe
    ctx.fillStyle = '#3A6A6A';
    ctx.fillRect(0, 3, s, 1);
    // Mystical rune-like markings
    ctx.fillStyle = '#4A2070';
    ctx.fillRect(3, 6, 2, 2);
    ctx.fillRect(8, 8, 2, 2);
    ctx.fillRect(12, 6, 2, 2);
    ctx.fillRect(5, 11, 2, 2);
    ctx.fillRect(10, 12, 2, 2);
    // Bottom edge
    ctx.fillStyle = '#0E0618';
    ctx.fillRect(0, s - 1, s, 1);
  });

  // Tile 87: Nether portal - purple swirling translucent pattern
  drawTile(87, (ctx, s, r) => {
    ctx.fillStyle = '#4A0077';
    ctx.fillRect(0, 0, s, s);
    // Swirling purple streaks
    for (let i = 0; i < 60; i++) {
      const shade = r();
      ctx.fillStyle = shade > 0.6 ? '#7722CC' : shade > 0.3 ? '#AA44FF' : '#5500AA';
      const x = Math.floor(r() * s);
      const y = Math.floor(r() * s);
      ctx.fillRect(x, y, r() > 0.5 ? 2 : 1, r() > 0.3 ? 3 : 2);
    }
    // Bright particles
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = '#DD88FF';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 88: Lava - orange/red flowing texture
  drawTile(88, (ctx, s, r) => {
    ctx.fillStyle = '#CC4400';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 50; i++) {
      const shade = r();
      ctx.fillStyle = shade > 0.7 ? '#FF6600' : shade > 0.4 ? '#FF4400' : shade > 0.2 ? '#DD3300' : '#FFAA00';
      const x = Math.floor(r() * s);
      const y = Math.floor(r() * s);
      ctx.fillRect(x, y, r() > 0.5 ? 3 : 2, r() > 0.5 ? 2 : 1);
    }
    // Bright hot spots
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = '#FFCC44';
      ctx.fillRect(Math.floor(r() * (s - 2)) + 1, Math.floor(r() * (s - 2)) + 1, 2, 2);
    }
  });

  // Tile 89: Nether gold ore - netherrack base with gold specks
  drawTile(89, (ctx, s, r) => {
    // Netherrack base
    ctx.fillStyle = '#7A2828';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#6A1818' : '#8A3838';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Gold specks
    for (let i = 0; i < 10; i++) {
      const cx = Math.floor(r() * (s - 2)) + 1;
      const cy = Math.floor(r() * (s - 2)) + 1;
      ctx.fillStyle = r() > 0.5 ? '#FFD700' : '#E8C030';
      ctx.fillRect(cx, cy, 2, 2);
      ctx.fillRect(cx + (r() > 0.5 ? 1 : -1), cy, 1, 1);
    }
  });

  // Tile 90: Nether quartz ore - netherrack base with white specks
  drawTile(90, (ctx, s, r) => {
    // Netherrack base
    ctx.fillStyle = '#7A2828';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#6A1818' : '#8A3838';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // White quartz specks
    for (let i = 0; i < 10; i++) {
      const cx = Math.floor(r() * (s - 2)) + 1;
      const cy = Math.floor(r() * (s - 2)) + 1;
      ctx.fillStyle = r() > 0.5 ? '#F0E8E0' : '#D8D0C8';
      ctx.fillRect(cx, cy, 2, 2);
      ctx.fillRect(cx + (r() > 0.5 ? 1 : -1), cy, 1, 1);
    }
  });

  // =============================================
  // REDSTONE SYSTEM TILES 91-96
  // =============================================

  // Tile 91: Redstone wire - red cross pattern on transparent background
  drawTile(91, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Transparent base with slight dark tint
    ctx.fillStyle = 'rgba(40, 0, 0, 0.2)';
    ctx.fillRect(0, 0, s, s);
    // Red wire cross pattern
    const cx = Math.floor(s / 2);
    ctx.fillStyle = '#CC1111';
    // Horizontal wire
    ctx.fillRect(0, cx - 1, s, 2);
    // Vertical wire
    ctx.fillRect(cx - 1, 0, 2, s);
    // Center dot (brighter)
    ctx.fillStyle = '#FF2222';
    ctx.fillRect(cx - 1, cx - 1, 2, 2);
    // Small specks of brightness along wires
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = r() > 0.5 ? '#DD3333' : '#AA0000';
      const pos = Math.floor(r() * s);
      if (r() > 0.5) {
        ctx.fillRect(pos, cx - 1, 1, 2);
      } else {
        ctx.fillRect(cx - 1, pos, 2, 1);
      }
    }
  });

  // Tile 92: Lever - stone base with stick
  drawTile(92, (ctx, s, r) => {
    // Stone base
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(3, s - 5, s - 6, 5);
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#6A6A6A' : '#8A8A8A';
      ctx.fillRect(3 + Math.floor(r() * (s - 6)), s - 5 + Math.floor(r() * 5), 1, 1);
    }
    // Darker border on base
    ctx.fillStyle = '#5A5A5A';
    ctx.fillRect(3, s - 5, s - 6, 1);
    ctx.fillRect(3, s - 1, s - 6, 1);
    // Stick (angled up)
    const cx = Math.floor(s / 2);
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(cx - 1, 2, 2, s - 7);
    ctx.fillStyle = '#7A5A30';
    ctx.fillRect(cx, 2, 1, s - 7);
    // Handle top
    ctx.fillStyle = '#555555';
    ctx.fillRect(cx - 1, 1, 2, 2);
  });

  // Tile 93: Button - small stone button
  drawTile(93, (ctx, s, r) => {
    // Transparent background
    ctx.clearRect(0, 0, s, s);
    // Stone background (wall hint)
    ctx.fillStyle = 'rgba(120, 120, 120, 0.3)';
    ctx.fillRect(0, 0, s, s);
    // Button itself (small raised stone rectangle)
    const bw = 6, bh = 4;
    const bx = Math.floor((s - bw) / 2);
    const by = Math.floor((s - bh) / 2);
    ctx.fillStyle = '#9A9A9A';
    ctx.fillRect(bx, by, bw, bh);
    // Highlight top edge
    ctx.fillStyle = '#BABABA';
    ctx.fillRect(bx, by, bw, 1);
    // Shadow bottom edge
    ctx.fillStyle = '#6A6A6A';
    ctx.fillRect(bx, by + bh - 1, bw, 1);
    // Side shadow
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(bx + bw - 1, by, 1, bh);
  });

  // Tile 94: Pressure plate - flat stone slab
  drawTile(94, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Flat stone plate with slight inset
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(1, 1, s - 2, s - 2);
    // Top highlight
    ctx.fillStyle = '#AAAAAA';
    ctx.fillRect(1, 1, s - 2, 1);
    ctx.fillRect(1, 1, 1, s - 2);
    // Bottom shadow
    ctx.fillStyle = '#6A6A6A';
    ctx.fillRect(1, s - 2, s - 2, 1);
    ctx.fillRect(s - 2, 1, 1, s - 2);
    // Stone texture noise
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A7A7A' : '#9A9A9A';
      ctx.fillRect(2 + Math.floor(r() * (s - 4)), 2 + Math.floor(r() * (s - 4)), 1, 1);
    }
  });

  // Tile 95: Redstone torch - stick with red flame
  drawTile(95, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    const cx = Math.floor(s / 2);
    // Stick
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(cx - 1, 5, 2, s - 5);
    ctx.fillStyle = '#7A5A30';
    ctx.fillRect(cx, 5, 1, s - 5);
    // Red flame/glow
    ctx.fillStyle = '#DD1111';
    ctx.fillRect(cx - 2, 2, 4, 4);
    ctx.fillStyle = '#FF2222';
    ctx.fillRect(cx - 1, 1, 2, 4);
    ctx.fillStyle = '#FF4444';
    ctx.fillRect(cx - 1, 2, 2, 2);
    // Flame tip
    ctx.fillStyle = '#FF3333';
    ctx.fillRect(cx, 0, 1, 2);
  });

  // Tile 96: Repeater - stone base with red torches and arrow
  drawTile(96, (ctx, s, r) => {
    // Stone base
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A7A7A' : '#9A9A9A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Border
    ctx.fillStyle = '#6A6A6A';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    // Two small red torches
    ctx.fillStyle = '#DD1111';
    ctx.fillRect(4, 5, 2, 3);
    ctx.fillRect(10, 5, 2, 3);
    ctx.fillStyle = '#FF3333';
    ctx.fillRect(4, 4, 2, 2);
    ctx.fillRect(10, 4, 2, 2);
    // Red line connecting (direction indicator)
    ctx.fillStyle = '#AA0000';
    ctx.fillRect(3, Math.floor(s / 2), s - 6, 1);
    // Arrow pointing right
    ctx.fillStyle = '#CC1111';
    ctx.fillRect(s - 4, Math.floor(s / 2) - 1, 2, 3);
  });

  // =============================================
  // INTERACTIVE BLOCKS TILES 97-103
  // =============================================

  // Tile 97: Trapdoor - wood planks pattern (horizontal slats)
  drawTile(97, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    // Horizontal wood planks
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = y % 8 === 0 ? '#9C7C4E' : '#7A5A30';
      ctx.fillRect(0, y, s, 3);
      ctx.fillStyle = '#6A4A20';
      ctx.fillRect(0, y + 3, s, 1);
    }
    // Iron hinge details at corners
    ctx.fillStyle = '#888888';
    ctx.fillRect(1, 1, 2, 2);
    ctx.fillRect(s - 3, 1, 2, 2);
    ctx.fillRect(1, s - 3, 2, 2);
    ctx.fillRect(s - 3, s - 3, 2, 2);
    // Border
    ctx.fillStyle = '#5A3A18';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
  });

  // Tile 98: Stairs stone - stone texture with angled step line
  drawTile(98, (ctx, s, r) => {
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(0, 0, s, s);
    // Stone noise
    for (let i = 0; i < 40; i++) {
      const shade = r();
      ctx.fillStyle = shade > 0.6 ? '#7A7A7A' : shade > 0.3 ? '#9A9A9A' : '#6A6A6A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), r() > 0.5 ? 2 : 1, 1);
    }
    // Step line (diagonal indicator)
    ctx.fillStyle = '#5A5A5A';
    ctx.fillRect(0, Math.floor(s / 2), s, 1);
    ctx.fillRect(Math.floor(s / 2), 0, 1, Math.floor(s / 2));
    // Top highlight
    ctx.fillStyle = '#AAAAAA';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(Math.floor(s / 2) + 1, Math.floor(s / 2) + 1, s / 2 - 1, 1);
  });

  // Tile 99: Stairs wood - plank texture with step line
  drawTile(99, (ctx, s, r) => {
    ctx.fillStyle = '#B89050';
    ctx.fillRect(0, 0, s, s);
    // Wood grain
    for (let y = 0; y < s; y += 2) {
      ctx.fillStyle = y % 4 === 0 ? '#A88040' : '#C8A060';
      ctx.fillRect(0, y, s, 1);
    }
    // Step line (diagonal indicator)
    ctx.fillStyle = '#7A5A30';
    ctx.fillRect(0, Math.floor(s / 2), s, 1);
    ctx.fillRect(Math.floor(s / 2), 0, 1, Math.floor(s / 2));
    // Top highlight
    ctx.fillStyle = '#D8B070';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(Math.floor(s / 2) + 1, Math.floor(s / 2) + 1, s / 2 - 1, 1);
  });

  // Tile 100: Sign - small brown rectangle on stick
  drawTile(100, (ctx, s, r) => {
    ctx.clearRect(0, 0, s, s);
    // Sign board (brown rectangle)
    ctx.fillStyle = '#B89050';
    ctx.fillRect(2, 1, s - 4, Math.floor(s * 0.6));
    // Board border
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(2, 1, s - 4, 1);
    ctx.fillRect(2, Math.floor(s * 0.6), s - 4, 1);
    ctx.fillRect(2, 1, 1, Math.floor(s * 0.6));
    ctx.fillRect(s - 3, 1, 1, Math.floor(s * 0.6));
    // Wood grain on sign
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = r() > 0.5 ? '#A88040' : '#C8A060';
      ctx.fillRect(3 + Math.floor(r() * (s - 6)), 2 + Math.floor(r() * (s * 0.6 - 3)), 2, 1);
    }
    // Stick (post)
    const cx = Math.floor(s / 2);
    ctx.fillStyle = '#7A5A30';
    ctx.fillRect(cx - 1, Math.floor(s * 0.6) + 1, 2, s - Math.floor(s * 0.6) - 1);
  });

  // Tile 101: Jukebox top - dark wood with circular hole
  drawTile(101, (ctx, s, r) => {
    ctx.fillStyle = '#6B4020';
    ctx.fillRect(0, 0, s, s);
    // Wood grain
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#5A3010' : '#7B5030';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Record slot (dark circle/square in center)
    const cx = Math.floor(s / 2);
    const cy = Math.floor(s / 2);
    ctx.fillStyle = '#1A1A1A';
    ctx.fillRect(cx - 3, cy - 3, 6, 6);
    ctx.fillStyle = '#2A2A2A';
    ctx.fillRect(cx - 2, cy - 2, 4, 4);
    // Center pin
    ctx.fillStyle = '#555555';
    ctx.fillRect(cx - 1, cy - 1, 2, 2);
  });

  // Tile 102: Jukebox side - dark brown wood with decorative line
  drawTile(102, (ctx, s, r) => {
    ctx.fillStyle = '#6B4020';
    ctx.fillRect(0, 0, s, s);
    // Wood grain
    for (let y = 0; y < s; y += 2) {
      ctx.fillStyle = y % 4 === 0 ? '#5A3010' : '#7B5030';
      ctx.fillRect(0, y, s, 1);
    }
    // Decorative horizontal groove
    ctx.fillStyle = '#4A2010';
    ctx.fillRect(1, Math.floor(s * 0.4), s - 2, 1);
    ctx.fillRect(1, Math.floor(s * 0.6), s - 2, 1);
    // Highlight above groove
    ctx.fillStyle = '#8B6040';
    ctx.fillRect(1, Math.floor(s * 0.4) - 1, s - 2, 1);
  });

  // Tile 103: Noteblock - wood with note symbol
  drawTile(103, (ctx, s, r) => {
    ctx.fillStyle = '#B89050';
    ctx.fillRect(0, 0, s, s);
    // Wood grain
    for (let y = 0; y < s; y += 2) {
      ctx.fillStyle = y % 4 === 0 ? '#A88040' : '#C8A060';
      ctx.fillRect(0, y, s, 1);
    }
    // Note symbol (simplified music note in center)
    const cx = Math.floor(s / 2);
    const cy = Math.floor(s / 2);
    // Note head (filled oval/circle)
    ctx.fillStyle = '#3A2010';
    ctx.fillRect(cx - 2, cy + 1, 3, 2);
    // Note stem
    ctx.fillRect(cx, cy - 4, 1, 6);
    // Note flag
    ctx.fillRect(cx + 1, cy - 4, 1, 1);
    ctx.fillRect(cx + 2, cy - 3, 1, 1);
    ctx.fillRect(cx + 2, cy - 2, 1, 1);
  });

  // =============================================
  // WHEAT GROWTH STAGES (tiles 104-106)
  // Tile 17 = stage 3 (mature), defined above
  // =============================================

  // Tile 104: Wheat stage 0 - small green sprouts
  drawTile(104, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    for (let x = 2; x < s; x += 4) {
      ctx.fillStyle = '#4CAF50';
      ctx.fillRect(x, s - 3, 1, 3);
    }
  });

  // Tile 105: Wheat stage 1 - taller green stalks
  drawTile(105, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    for (let x = 2; x < s; x += 3) {
      ctx.fillStyle = '#5B8C2A';
      ctx.fillRect(x, s - 7, 1, 7);
    }
  });

  // Tile 106: Wheat stage 2 - tall yellow-green stalks with small heads
  drawTile(106, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    for (let x = 1; x < s; x += 3) {
      ctx.fillStyle = '#7CB342';
      ctx.fillRect(x, 4, 1, s - 4);
      ctx.fillStyle = '#ACA030';
      ctx.fillRect(x - 1, 2, 3, 3);
    }
  });

  // =============================================
  // END DIMENSION TILES (113-122)
  // =============================================

  // Tile 113: End portal frame top (dark green stone with eye socket)
  drawTile(113, (ctx, s, r) => {
    ctx.fillStyle = '#2E4032';
    ctx.fillRect(0, 0, s, s);
    for (let x = 0; x < s; x++) {
      for (let y = 0; y < s; y++) {
        if (r() > 0.7) { ctx.fillStyle = r() > 0.5 ? '#3A5040' : '#223028'; ctx.fillRect(x, y, 1, 1); }
      }
    }
    // Eye socket in center
    ctx.fillStyle = '#0A0A0A';
    ctx.fillRect(5, 5, 6, 6);
    ctx.fillStyle = '#1A5A3A';
    ctx.fillRect(6, 6, 4, 4);
    ctx.fillStyle = '#0EE8A0';
    ctx.fillRect(7, 7, 2, 2);
  });

  // Tile 114: End portal frame side (carved dark stone)
  drawTile(114, (ctx, s, r) => {
    ctx.fillStyle = '#2E4032';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = r() > 0.5 ? '#3A5040' : '#1E3028';
      ctx.fillRect(0, y, s, 2);
    }
    // Carved pattern
    ctx.fillStyle = '#1A2A1E';
    ctx.fillRect(2, 3, 2, 10);
    ctx.fillRect(12, 3, 2, 10);
    ctx.fillRect(6, 6, 4, 4);
  });

  // Tile 115: End portal active (starfield black with tiny stars)
  drawTile(115, (ctx, s, r) => {
    ctx.fillStyle = '#0A0012';
    ctx.fillRect(0, 0, s, s);
    // Stars
    for (let i = 0; i < 20; i++) {
      const sx = Math.floor(r() * s);
      const sy = Math.floor(r() * s);
      ctx.fillStyle = r() > 0.7 ? '#FFFFFF' : (r() > 0.5 ? '#8866FF' : '#44AAFF');
      ctx.fillRect(sx, sy, 1, 1);
    }
    // Swirl hint
    ctx.fillStyle = 'rgba(100, 60, 180, 0.3)';
    ctx.fillRect(4, 4, 8, 8);
  });

  // Tile 116: Purpur block (purple stone)
  drawTile(116, (ctx, s, r) => {
    ctx.fillStyle = '#9B59B6';
    ctx.fillRect(0, 0, s, s);
    for (let x = 0; x < s; x++) {
      for (let y = 0; y < s; y++) {
        if (r() > 0.6) { ctx.fillStyle = r() > 0.5 ? '#A96BC4' : '#8E4DA8'; ctx.fillRect(x, y, 1, 1); }
      }
    }
    // Subtle grid
    ctx.fillStyle = '#7B3F96';
    for (let i = 0; i < s; i += 4) { ctx.fillRect(i, 0, 1, s); ctx.fillRect(0, i, s, 1); }
  });

  // Tile 117: Purpur pillar top (concentric circles)
  drawTile(117, (ctx, s, r) => {
    ctx.fillStyle = '#9B59B6';
    ctx.fillRect(0, 0, s, s);
    const cx = s / 2, cy = s / 2;
    for (let rad = 6; rad >= 2; rad -= 2) {
      ctx.fillStyle = rad % 4 === 0 ? '#B070CC' : '#7B3F96';
      ctx.beginPath();
      ctx.arc(cx, cy, rad, 0, Math.PI * 2);
      ctx.fill();
    }
  });

  // Tile 118: Purpur pillar side (vertical stripes)
  drawTile(118, (ctx, s, r) => {
    ctx.fillStyle = '#9B59B6';
    ctx.fillRect(0, 0, s, s);
    for (let x = 0; x < s; x += 3) {
      ctx.fillStyle = r() > 0.5 ? '#B070CC' : '#7B3F96';
      ctx.fillRect(x, 0, 2, s);
    }
    // Horizontal bands
    ctx.fillStyle = '#6A2E86';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
  });

  // Tile 119: End stone bricks
  drawTile(119, (ctx, s, r) => {
    ctx.fillStyle = '#D4C99E';
    ctx.fillRect(0, 0, s, s);
    // Brick pattern
    ctx.fillStyle = '#BAB080';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y + 3, s, 1);
      const offset = (y % 8 === 0) ? 0 : 4;
      for (let x = offset; x < s; x += 8) {
        ctx.fillRect(x, y, 1, 4);
      }
    }
    // Noise
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#C8BE90' : '#A89870';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 120: Chorus plant (dark purple organic)
  drawTile(120, (ctx, s, r) => {
    ctx.fillStyle = '#7D3C98';
    ctx.fillRect(0, 0, s, s);
    // Organic texture
    for (let x = 0; x < s; x++) {
      for (let y = 0; y < s; y++) {
        if (r() > 0.5) { ctx.fillStyle = r() > 0.6 ? '#9B59B6' : '#5B2C6F'; ctx.fillRect(x, y, 1, 1); }
      }
    }
    // Stem center
    ctx.fillStyle = '#4A1A6B';
    ctx.fillRect(6, 0, 4, s);
    ctx.fillRect(0, 6, s, 4);
  });

  // Tile 121: Dragon egg (dark gradient with purple specks)
  drawTile(121, (ctx, s, r) => {
    // Dark base
    ctx.fillStyle = '#1A0A2E';
    ctx.fillRect(0, 0, s, s);
    // Gradient darker at edges
    for (let x = 0; x < s; x++) {
      for (let y = 0; y < s; y++) {
        const dist = Math.sqrt((x - 8) ** 2 + (y - 8) ** 2) / 8;
        if (r() > 0.7) {
          const v = Math.max(0, 0.3 - dist * 0.2);
          ctx.fillStyle = `rgba(140, 60, 200, ${v})`;
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
    // Purple specks
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = '#6C3483';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 122: End rod (thin glowing white-purple)
  drawTile(122, (ctx, s, r) => {
    ctx.fillStyle = '#E8D8F0';
    ctx.fillRect(0, 0, s, s);
    // Glow
    ctx.fillStyle = '#F0E8FF';
    ctx.fillRect(4, 0, 8, s);
    // Rod center
    ctx.fillStyle = '#D8C8E8';
    ctx.fillRect(6, 0, 4, s);
    // Purple bands
    ctx.fillStyle = '#B090D0';
    ctx.fillRect(6, 3, 4, 1);
    ctx.fillRect(6, 8, 4, 1);
    ctx.fillRect(6, 13, 4, 1);
  });

  // =============================================
  // GRAVE SYSTEM TILES (107-112)
  // =============================================

  // Tile 107: Coffin top (lid) - dark wood with metal trim
  drawTile(107, (ctx, s, r) => {
    ctx.fillStyle = '#3B2316';
    ctx.fillRect(0, 0, s, s);
    // Wood grain
    for (let y = 0; y < s; y += 3) {
      ctx.fillStyle = r() > 0.5 ? '#4A2E1C' : '#352013';
      ctx.fillRect(0, y, s, 1);
    }
    // Metal trim border
    ctx.fillStyle = '#5A5A5A';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    // Cross on lid
    ctx.fillStyle = '#6B6B6B';
    ctx.fillRect(7, 3, 2, 10);
    ctx.fillRect(4, 5, 8, 2);
  });

  // Tile 108: Coffin side - dark wood planks
  drawTile(108, (ctx, s, r) => {
    ctx.fillStyle = '#3B2316';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = r() > 0.5 ? '#4A2E1C' : '#301B0E';
      ctx.fillRect(0, y, s, 2);
      ctx.fillStyle = '#2A1508';
      ctx.fillRect(0, y + 3, s, 1);
    }
    // Metal band
    ctx.fillStyle = '#5A5A5A';
    ctx.fillRect(0, 7, s, 2);
    // Nails
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(3, 7, 1, 2);
    ctx.fillRect(12, 7, 1, 2);
  });

  // Tile 109: Coffin open top - red velvet interior
  drawTile(109, (ctx, s, r) => {
    // Dark wood border
    ctx.fillStyle = '#3B2316';
    ctx.fillRect(0, 0, s, s);
    // Red velvet interior
    ctx.fillStyle = '#8B1A1A';
    ctx.fillRect(2, 2, s - 4, s - 4);
    // Velvet texture
    for (let x = 2; x < s - 2; x++) {
      for (let y = 2; y < s - 2; y++) {
        if (r() > 0.7) {
          ctx.fillStyle = r() > 0.5 ? '#9C2020' : '#7A1515';
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
    // Pillow area at top
    ctx.fillStyle = '#C0C0C0';
    ctx.fillRect(4, 3, s - 8, 3);
    ctx.fillStyle = '#A8A8A8';
    ctx.fillRect(5, 4, s - 10, 1);
  });

  // Tile 110: Cross - wooden cross shape
  drawTile(110, (ctx, s, r) => {
    // Transparent background (sky-like)
    ctx.fillStyle = '#87CEEB';
    ctx.fillRect(0, 0, s, s);
    // Vertical beam
    ctx.fillStyle = '#6B4226';
    ctx.fillRect(6, 1, 4, 14);
    // Horizontal beam
    ctx.fillRect(2, 4, 12, 3);
    // Wood grain
    ctx.fillStyle = '#7A4E2E';
    ctx.fillRect(7, 2, 1, 12);
    ctx.fillRect(3, 5, 10, 1);
    // Dark outline
    ctx.fillStyle = '#3B2316';
    ctx.fillRect(6, 0, 4, 1);
    ctx.fillRect(6, 15, 4, 1);
    ctx.fillRect(1, 4, 1, 3);
    ctx.fillRect(14, 4, 1, 3);
  });

  // Tile 111: Grave base top - dark disturbed dirt
  drawTile(111, (ctx, s, r) => {
    ctx.fillStyle = '#3D2B1A';
    ctx.fillRect(0, 0, s, s);
    // Disturbed dirt texture
    for (let x = 0; x < s; x++) {
      for (let y = 0; y < s; y++) {
        if (r() > 0.6) {
          ctx.fillStyle = r() > 0.5 ? '#4A3520' : '#2E1F10';
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
    // Small cracks
    ctx.fillStyle = '#1A0F05';
    ctx.fillRect(3, 5, 4, 1);
    ctx.fillRect(9, 10, 5, 1);
    ctx.fillRect(6, 2, 1, 3);
  });

  // Tile 112: Coffin front (head end) - dark wood with cross
  drawTile(112, (ctx, s, r) => {
    ctx.fillStyle = '#3B2316';
    ctx.fillRect(0, 0, s, s);
    // Wood texture
    for (let x = 0; x < s; x += 3) {
      ctx.fillStyle = r() > 0.5 ? '#4A2E1C' : '#30180E';
      ctx.fillRect(x, 0, 2, s);
    }
    // Small cross emblem
    ctx.fillStyle = '#6B6B6B';
    ctx.fillRect(7, 4, 2, 8);
    ctx.fillRect(4, 6, 8, 2);
  });

  // =============================================
  // FARMING & VILLAGE TILES 123-130 (Fase 5)
  // =============================================

  // Tile 123: Carrot - brown soil with orange carrot root, green leafy top
  drawTile(123, (ctx, s, r) => {
    // Brown soil base
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#5A3E20' : '#7A5A38';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Orange carrot root poking up
    ctx.fillStyle = '#E87020';
    ctx.fillRect(6, 8, 3, 6);
    ctx.fillStyle = '#D06018';
    ctx.fillRect(7, 9, 1, 5);
    // Green leafy top
    ctx.fillStyle = '#3A8A20';
    ctx.fillRect(5, 3, 6, 5);
    ctx.fillStyle = '#4AAA30';
    ctx.fillRect(6, 2, 4, 3);
    ctx.fillStyle = '#2A6A10';
    ctx.fillRect(7, 4, 2, 2);
  });

  // Tile 124: Potato - brown soil with tan potato bumps
  drawTile(124, (ctx, s, r) => {
    // Brown soil base
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#5A3E20' : '#7A5A38';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Tan potato bumps visible in soil
    ctx.fillStyle = '#C4A060';
    ctx.fillRect(3, 9, 4, 4);
    ctx.fillStyle = '#B89050';
    ctx.fillRect(9, 10, 4, 3);
    ctx.fillStyle = '#D4B070';
    ctx.fillRect(4, 10, 2, 2);
    // Small green sprout
    ctx.fillStyle = '#3A8A20';
    ctx.fillRect(6, 3, 4, 5);
    ctx.fillStyle = '#4AAA30';
    ctx.fillRect(7, 2, 2, 3);
  });

  // Tile 125: Beetroot - brown soil with dark red beetroot top, green leaves
  drawTile(125, (ctx, s, r) => {
    // Brown soil base
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#5A3E20' : '#7A5A38';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Dark red beetroot poking out
    ctx.fillStyle = '#8B1A1A';
    ctx.fillRect(5, 8, 5, 5);
    ctx.fillStyle = '#A02020';
    ctx.fillRect(6, 9, 3, 3);
    // Green leaves
    ctx.fillStyle = '#3A8A20';
    ctx.fillRect(4, 2, 7, 5);
    ctx.fillStyle = '#4AAA30';
    ctx.fillRect(6, 1, 3, 3);
    ctx.fillStyle = '#2A6A10';
    ctx.fillRect(5, 4, 5, 2);
  });

  // Tile 126: Berry bush - green bush with small red dots (berries)
  drawTile(126, (ctx, s, r) => {
    // Green bush base
    ctx.fillStyle = '#2A6A10';
    ctx.fillRect(0, 0, s, s);
    for (let x = 0; x < s; x++) {
      for (let y = 0; y < s; y++) {
        if (r() > 0.6) {
          ctx.fillStyle = r() > 0.5 ? '#3A8A20' : '#1A5A08';
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
    // Small red berries scattered
    ctx.fillStyle = '#DD2222';
    ctx.fillRect(3, 4, 2, 2);
    ctx.fillRect(10, 3, 2, 2);
    ctx.fillRect(6, 9, 2, 2);
    ctx.fillRect(12, 10, 2, 2);
    ctx.fillRect(2, 11, 2, 2);
    // Berry highlights
    ctx.fillStyle = '#FF4444';
    ctx.fillRect(3, 4, 1, 1);
    ctx.fillRect(10, 3, 1, 1);
    ctx.fillRect(6, 9, 1, 1);
  });

  // Tile 127: Pumpkin stem - thin green stalk/vine on brown soil
  drawTile(127, (ctx, s, r) => {
    // Brown soil base
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#5A3E20' : '#7A5A38';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Thin green stalk
    ctx.fillStyle = '#3A8A20';
    ctx.fillRect(7, 2, 2, 12);
    ctx.fillStyle = '#4AAA30';
    ctx.fillRect(7, 2, 1, 10);
    // Small leaves
    ctx.fillStyle = '#2A6A10';
    ctx.fillRect(5, 4, 2, 2);
    ctx.fillRect(9, 6, 2, 2);
    // Vine curl
    ctx.fillStyle = '#3A8A20';
    ctx.fillRect(4, 3, 1, 1);
    ctx.fillRect(10, 5, 1, 1);
  });

  // Tile 128: Melon stem - similar to pumpkin stem but slightly different green
  drawTile(128, (ctx, s, r) => {
    // Brown soil base
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#5A3E20' : '#7A5A38';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Thin green stalk (slightly different green)
    ctx.fillStyle = '#4A9A30';
    ctx.fillRect(7, 2, 2, 12);
    ctx.fillStyle = '#5AAA40';
    ctx.fillRect(7, 3, 1, 9);
    // Small leaves
    ctx.fillStyle = '#3A7A18';
    ctx.fillRect(5, 5, 2, 2);
    ctx.fillRect(9, 3, 2, 2);
    // Vine curl
    ctx.fillStyle = '#4A9A30';
    ctx.fillRect(4, 4, 1, 1);
    ctx.fillRect(10, 2, 1, 1);
  });

  // Tile 129: Sugar cane - vertical green bamboo-like segments with nodes
  drawTile(129, (ctx, s, r) => {
    // Light background (sky visible through)
    ctx.fillStyle = '#87CEEB';
    ctx.fillRect(0, 0, s, s);
    // Main cane segment
    ctx.fillStyle = '#5AAA40';
    ctx.fillRect(5, 0, 5, s);
    // Lighter inner stripe
    ctx.fillStyle = '#6ABB50';
    ctx.fillRect(6, 0, 3, s);
    ctx.fillStyle = '#7ACC60';
    ctx.fillRect(7, 0, 1, s);
    // Dark nodes/joints
    ctx.fillStyle = '#3A7A18';
    ctx.fillRect(5, 3, 5, 1);
    ctx.fillRect(5, 8, 5, 1);
    ctx.fillRect(5, 13, 5, 1);
    // Small leaf stubs at nodes
    ctx.fillStyle = '#4A9A30';
    ctx.fillRect(10, 2, 2, 2);
    ctx.fillRect(3, 7, 2, 2);
    ctx.fillRect(10, 12, 2, 2);
  });

  // Tile 130: Bell - gold/yellow bell shape on dark background
  drawTile(130, (ctx, s, r) => {
    // Dark stone background
    ctx.fillStyle = '#4A4A4A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#3A3A3A' : '#5A5A5A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Bell top mount
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(6, 1, 4, 2);
    // Gold bell body
    ctx.fillStyle = '#FFD700';
    ctx.fillRect(5, 3, 6, 4);
    ctx.fillRect(4, 7, 8, 2);
    ctx.fillRect(3, 9, 10, 3);
    // Bell highlight
    ctx.fillStyle = '#FFE84A';
    ctx.fillRect(6, 4, 2, 3);
    // Bell shadow
    ctx.fillStyle = '#D4AA00';
    ctx.fillRect(9, 5, 1, 4);
    ctx.fillRect(4, 10, 8, 1);
    // Clapper
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(7, 11, 2, 2);
  });

  // =============================================
  // REDSTONE AUTOMATION TILES 131-147 (Fase 6)
  // =============================================

  // Tile 131: Piston face (wood circle pattern on stone)
  drawTile(131, (ctx, s, r) => {
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A7A7A' : '#9A9A9A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Wood circle in center
    ctx.fillStyle = '#B89050';
    ctx.fillRect(4, 4, 8, 8);
    ctx.fillStyle = '#A08040';
    ctx.fillRect(5, 5, 6, 6);
    ctx.fillStyle = '#C8A060';
    ctx.fillRect(6, 6, 4, 4);
    // Center dot
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(7, 7, 2, 2);
  });

  // Tile 132: Piston bottom (planks)
  drawTile(132, (ctx, s, r) => {
    ctx.fillStyle = '#B89050';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      ctx.fillStyle = '#A08040';
      ctx.fillRect(0, y, s, 1);
    }
    for (let x = 0; x < s; x += 4) {
      ctx.fillStyle = '#C8A060';
      ctx.fillRect(x, 0, 1, s);
    }
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = r() > 0.5 ? '#C8A060' : '#A08040';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 133: Piston side (stone top half, wood bottom half)
  drawTile(133, (ctx, s, r) => {
    // Stone top half
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(0, 0, s, s / 2);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A7A7A' : '#9A9A9A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * (s / 2)), 1, 1);
    }
    // Wood bottom half
    ctx.fillStyle = '#B89050';
    ctx.fillRect(0, s / 2, s, s / 2);
    for (let y = s / 2; y < s; y += 3) {
      ctx.fillStyle = '#A08040';
      ctx.fillRect(0, y, s, 1);
    }
    // Border between halves
    ctx.fillStyle = '#6A6A6A';
    ctx.fillRect(0, s / 2 - 1, s, 1);
  });

  // Tile 134: Piston head (flat wood platform)
  drawTile(134, (ctx, s, r) => {
    ctx.fillStyle = '#C8A060';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 3) {
      ctx.fillStyle = '#B09050';
      ctx.fillRect(0, y, s, 1);
    }
    for (let x = 0; x < s; x += 4) {
      ctx.fillStyle = '#D4B070';
      ctx.fillRect(x, 0, 1, s);
    }
    // Border
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
  });

  // Tile 135: Sticky piston face (like 131 but with green slime center)
  drawTile(135, (ctx, s, r) => {
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A7A7A' : '#9A9A9A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Green slime center
    ctx.fillStyle = '#66CC44';
    ctx.fillRect(3, 3, 10, 10);
    ctx.fillStyle = '#55BB33';
    ctx.fillRect(4, 4, 8, 8);
    ctx.fillStyle = '#77DD55';
    ctx.fillRect(5, 5, 4, 4);
    // Slime highlights
    ctx.fillStyle = '#88EE66';
    ctx.fillRect(5, 5, 2, 2);
  });

  // Tile 136: Hopper (dark iron #444 with funnel shape)
  drawTile(136, (ctx, s, r) => {
    ctx.fillStyle = '#444444';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#3A3A3A' : '#555555';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Funnel shape: wide at top, narrow at bottom
    ctx.fillStyle = '#333333';
    ctx.fillRect(0, 0, s, 3);
    ctx.fillRect(2, 3, s - 4, 3);
    ctx.fillRect(4, 6, s - 8, 3);
    ctx.fillRect(5, 9, s - 10, 4);
    ctx.fillRect(6, 13, s - 12, 3);
    // Rim highlight
    ctx.fillStyle = '#666666';
    ctx.fillRect(1, 0, s - 2, 1);
    // Interior darkness
    ctx.fillStyle = '#222222';
    ctx.fillRect(3, 1, s - 6, 2);
  });

  // Tile 137: Dropper/Dispenser top (cobblestone)
  drawTile(137, (ctx, s, r) => {
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      for (let x = 0; x < s; x += 4) {
        const offset = (y / 4) % 2 === 0 ? 0 : 2;
        const shade = r();
        ctx.fillStyle = shade > 0.6 ? '#8A8A8A' : shade > 0.3 ? '#6A6A6A' : '#5A5A5A';
        ctx.fillRect(x + offset, y, 3, 3);
      }
    }
    ctx.fillStyle = '#555555';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y, s, 1);
    }
  });

  // Tile 138: Dropper front (cobblestone with dark hole)
  drawTile(138, (ctx, s, r) => {
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      for (let x = 0; x < s; x += 4) {
        const offset = (y / 4) % 2 === 0 ? 0 : 2;
        const shade = r();
        ctx.fillStyle = shade > 0.6 ? '#8A8A8A' : shade > 0.3 ? '#6A6A6A' : '#5A5A5A';
        ctx.fillRect(x + offset, y, 3, 3);
      }
    }
    // Dark hole in center
    ctx.fillStyle = '#222222';
    ctx.fillRect(5, 5, 6, 6);
    ctx.fillStyle = '#111111';
    ctx.fillRect(6, 6, 4, 4);
  });

  // Tile 139: Dispenser front (cobblestone with red mouth/opening)
  drawTile(139, (ctx, s, r) => {
    ctx.fillStyle = '#7A7A7A';
    ctx.fillRect(0, 0, s, s);
    for (let y = 0; y < s; y += 4) {
      for (let x = 0; x < s; x += 4) {
        const offset = (y / 4) % 2 === 0 ? 0 : 2;
        const shade = r();
        ctx.fillStyle = shade > 0.6 ? '#8A8A8A' : shade > 0.3 ? '#6A6A6A' : '#5A5A5A';
        ctx.fillRect(x + offset, y, 3, 3);
      }
    }
    // Red mouth opening
    ctx.fillStyle = '#333333';
    ctx.fillRect(4, 4, 8, 8);
    ctx.fillStyle = '#8B2020';
    ctx.fillRect(5, 5, 6, 6);
    ctx.fillStyle = '#AA3030';
    ctx.fillRect(6, 6, 4, 3);
    // Teeth-like top/bottom
    ctx.fillStyle = '#6A6A6A';
    ctx.fillRect(5, 5, 6, 1);
    ctx.fillRect(5, 10, 6, 1);
  });

  // Tile 140: Observer top/bottom (stone with ridges)
  drawTile(140, (ctx, s, r) => {
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A7A7A' : '#9A9A9A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Ridge lines
    ctx.fillStyle = '#6A6A6A';
    ctx.fillRect(0, 3, s, 1);
    ctx.fillRect(0, 7, s, 1);
    ctx.fillRect(0, 11, s, 1);
    ctx.fillStyle = '#AAAAAA';
    ctx.fillRect(0, 4, s, 1);
    ctx.fillRect(0, 8, s, 1);
    ctx.fillRect(0, 12, s, 1);
  });

  // Tile 141: Observer side (stone with arrow pattern)
  drawTile(141, (ctx, s, r) => {
    ctx.fillStyle = '#8A8A8A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A7A7A' : '#9A9A9A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
    // Arrow pointing right
    ctx.fillStyle = '#DD2222';
    ctx.fillRect(3, 7, 7, 2);   // shaft
    ctx.fillRect(8, 5, 2, 6);   // arrowhead
    ctx.fillRect(10, 6, 1, 4);
    ctx.fillRect(11, 7, 1, 2);
    // Red face on the back
    ctx.fillStyle = '#CC1111';
    ctx.fillRect(0, 4, 2, 8);
  });

  // Tile 142: Redstone lamp off (brownish glass, dark)
  drawTile(142, (ctx, s, r) => {
    ctx.fillStyle = '#8A6A3A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A5A2A' : '#9A7A4A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Redstone accent lines
    ctx.fillStyle = '#662222';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    // Cross pattern
    ctx.fillRect(s / 2, 0, 1, s);
    ctx.fillRect(0, s / 2, s, 1);
    // Dark glass center
    ctx.fillStyle = '#6A4A2A';
    ctx.fillRect(2, 2, 5, 5);
    ctx.fillRect(9, 2, 5, 5);
    ctx.fillRect(2, 9, 5, 5);
    ctx.fillRect(9, 9, 5, 5);
  });

  // Tile 143: Redstone lamp lit (bright yellowish glow like glowstone)
  drawTile(143, (ctx, s, r) => {
    ctx.fillStyle = '#EEDD60';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#FFEE80' : '#D4BB40';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Redstone accent lines (glowing)
    ctx.fillStyle = '#DD4444';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    ctx.fillRect(s / 2, 0, 1, s);
    ctx.fillRect(0, s / 2, s, 1);
    // Bright glow centers
    ctx.fillStyle = '#FFFF99';
    ctx.fillRect(3, 3, 4, 4);
    ctx.fillRect(9, 3, 4, 4);
    ctx.fillRect(3, 9, 4, 4);
    ctx.fillRect(9, 9, 4, 4);
  });

  // Tile 144: Daylight sensor (blue glass panel)
  drawTile(144, (ctx, s, r) => {
    ctx.fillStyle = '#6688BB';
    ctx.fillRect(0, 0, s, s);
    // Glass grid pattern
    ctx.fillStyle = '#7799CC';
    for (let x = 0; x < s; x += 4) {
      ctx.fillRect(x, 0, 3, s);
    }
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(0, y, s, 3);
    }
    // Frame
    ctx.fillStyle = '#B89050';
    ctx.fillRect(0, 0, s, 1);
    ctx.fillRect(0, s - 1, s, 1);
    ctx.fillRect(0, 0, 1, s);
    ctx.fillRect(s - 1, 0, 1, s);
    // Shine
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillRect(2, 2, 3, 2);
  });

  // Tile 145: Powered rail (gold colored rail tracks)
  drawTile(145, (ctx, s, r) => {
    // Background (wood ties)
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    // Wooden ties
    ctx.fillStyle = '#8B6B3E';
    for (let y = 1; y < s; y += 4) {
      ctx.fillRect(1, y, s - 2, 2);
    }
    // Gold rails
    ctx.fillStyle = '#FFD700';
    ctx.fillRect(3, 0, 2, s);
    ctx.fillRect(s - 5, 0, 2, s);
    // Rail highlights
    ctx.fillStyle = '#FFE84A';
    ctx.fillRect(3, 0, 1, s);
    ctx.fillRect(s - 5, 0, 1, s);
    // Redstone line in center
    ctx.fillStyle = '#DD2222';
    ctx.fillRect(7, 0, 2, s);
  });

  // Tile 146: Detector rail (iron rail with redstone accent)
  drawTile(146, (ctx, s, r) => {
    // Background (wood ties)
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    // Wooden ties
    ctx.fillStyle = '#8B6B3E';
    for (let y = 1; y < s; y += 4) {
      ctx.fillRect(1, y, s - 2, 2);
    }
    // Iron rails
    ctx.fillStyle = '#B0B0B0';
    ctx.fillRect(3, 0, 2, s);
    ctx.fillRect(s - 5, 0, 2, s);
    // Rail highlights
    ctx.fillStyle = '#C8C8C8';
    ctx.fillRect(3, 0, 1, s);
    ctx.fillRect(s - 5, 0, 1, s);
    // Redstone pressure plate in center
    ctx.fillStyle = '#AA2222';
    ctx.fillRect(5, 3, 6, 2);
    ctx.fillRect(5, 7, 6, 2);
    ctx.fillRect(5, 11, 6, 2);
  });

  // Tile 147: Rail (simple iron rail tracks)
  drawTile(147, (ctx, s, r) => {
    // Background (wood ties)
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, s);
    // Wooden ties
    ctx.fillStyle = '#8B6B3E';
    for (let y = 1; y < s; y += 4) {
      ctx.fillRect(1, y, s - 2, 2);
    }
    // Iron rails
    ctx.fillStyle = '#B0B0B0';
    ctx.fillRect(3, 0, 2, s);
    ctx.fillRect(s - 5, 0, 2, s);
    // Rail highlights
    ctx.fillStyle = '#C8C8C8';
    ctx.fillRect(3, 0, 1, s);
    ctx.fillRect(s - 5, 0, 1, s);
    // Darker center
    ctx.fillStyle = '#5A4020';
    ctx.fillRect(6, 0, 4, s);
  });

  // =============================================
  // EXPLORATION TILES 148-159 (Fase 7)
  // =============================================

  // Tile 148: Dripstone (rough beige stone with drip marks)
  drawTile(148, (ctx, s, r) => {
    ctx.fillStyle = '#B8A88A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#A89878' : '#C8B89A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
    // Drip marks (vertical streaks)
    ctx.fillStyle = 'rgba(100,140,160,0.3)';
    for (let i = 0; i < 4; i++) {
      const dx = Math.floor(r() * (s - 1));
      ctx.fillRect(dx, Math.floor(r() * 4), 1, Math.floor(r() * 8) + 4);
    }
  });

  // Tile 149: Pointed dripstone (stalactite shape, thin)
  drawTile(149, (ctx, s, r) => {
    ctx.fillStyle = '#A89070';
    ctx.fillRect(0, 0, s, s);
    // Stalactite shape: triangle narrowing downward
    ctx.fillStyle = '#C8B090';
    ctx.beginPath();
    ctx.moveTo(s * 0.2, 0);
    ctx.lineTo(s * 0.8, 0);
    ctx.lineTo(s * 0.5, s);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#B8A080';
    ctx.beginPath();
    ctx.moveTo(s * 0.3, 0);
    ctx.lineTo(s * 0.7, 0);
    ctx.lineTo(s * 0.5, s * 0.9);
    ctx.closePath();
    ctx.fill();
    // Texture noise
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#9A8060' : '#D0C0A0';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 150: Azalea (green bush with pink flowers)
  drawTile(150, (ctx, s, r) => {
    ctx.fillStyle = '#4A8A30';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 25; i++) {
      ctx.fillStyle = r() > 0.5 ? '#3A7A20' : '#5A9A40';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Pink flowers
    ctx.fillStyle = '#E87AAF';
    for (let i = 0; i < 6; i++) {
      ctx.fillRect(Math.floor(r() * (s - 2)), Math.floor(r() * (s - 2)), 2, 2);
    }
    ctx.fillStyle = '#F09AC0';
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(Math.floor(r() * (s - 1)), Math.floor(r() * (s - 1)), 1, 1);
    }
  });

  // Tile 151: Azalea leaves (light green with pink specks)
  drawTile(151, (ctx, s, r) => {
    ctx.fillStyle = '#6BAA45';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#5A9A35' : '#7CBA55';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Pink specks
    ctx.fillStyle = '#E88AAF';
    for (let i = 0; i < 5; i++) {
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 152: Glow berry vine (dark green vine with yellow glowing dots)
  drawTile(152, (ctx, s, r) => {
    ctx.fillStyle = '#2A5A18';
    ctx.fillRect(0, 0, s, s);
    // Vine strands
    ctx.fillStyle = '#3A6A28';
    for (let x = 2; x < s; x += 4) {
      ctx.fillRect(x, 0, 2, s);
    }
    ctx.fillStyle = '#1A4A10';
    for (let i = 0; i < 10; i++) {
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 2);
    }
    // Glowing yellow berries
    ctx.fillStyle = '#FFD744';
    for (let i = 0; i < 5; i++) {
      const bx = Math.floor(r() * (s - 2));
      const by = Math.floor(r() * (s - 2));
      ctx.fillRect(bx, by, 2, 2);
    }
    // Glow halo
    ctx.fillStyle = 'rgba(255,220,60,0.25)';
    for (let i = 0; i < 5; i++) {
      const bx = Math.floor(r() * (s - 3));
      const by = Math.floor(r() * (s - 3));
      ctx.fillRect(bx, by, 3, 3);
    }
  });

  // Tile 153: Sculk (dark teal-blue with veins, #0C4A4E)
  drawTile(153, (ctx, s, r) => {
    ctx.fillStyle = '#0C4A4E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = r() > 0.5 ? '#0A3A3E' : '#0E5A5E';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Vein-like patterns
    ctx.fillStyle = '#06AAB0';
    for (let i = 0; i < 8; i++) {
      const vx = Math.floor(r() * s);
      const vy = Math.floor(r() * s);
      ctx.fillRect(vx, vy, 1, Math.floor(r() * 3) + 1);
    }
  });

  // Tile 154: Sculk sensor (sculk with gold tendrils)
  drawTile(154, (ctx, s, r) => {
    ctx.fillStyle = '#0C4A4E';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 15; i++) {
      ctx.fillStyle = r() > 0.5 ? '#0A3A3E' : '#0E5A5E';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Gold/amber tendrils on top
    ctx.fillStyle = '#D4AA30';
    for (let i = 0; i < 5; i++) {
      const tx = Math.floor(r() * (s - 1));
      ctx.fillRect(tx, Math.floor(r() * (s / 2)), 1, Math.floor(r() * 4) + 2);
    }
    ctx.fillStyle = '#E8C840';
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * 3), 2, 1);
    }
  });

  // Tile 155: Moss block (rich green fuzzy texture)
  drawTile(155, (ctx, s, r) => {
    ctx.fillStyle = '#3A7A20';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#2A6A15' : '#4A8A30';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Fuzzy specks
    ctx.fillStyle = '#5AAA40';
    for (let i = 0; i < 10; i++) {
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 156: Mycelium top (purple-gray fungal)
  drawTile(156, (ctx, s, r) => {
    ctx.fillStyle = '#8A7A8A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = r() > 0.5 ? '#7A6A7A' : '#9A8A9A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 2);
    }
    // Fungal purple dots
    ctx.fillStyle = '#AA88BB';
    for (let i = 0; i < 8; i++) {
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 1, 1);
    }
  });

  // Tile 157: Terracotta (clay-brown)
  drawTile(157, (ctx, s, r) => {
    ctx.fillStyle = '#9A6B48';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 25; i++) {
      ctx.fillStyle = r() > 0.5 ? '#8A5B38' : '#AA7B58';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 158: Terracotta red (terracotta with red tint)
  drawTile(158, (ctx, s, r) => {
    ctx.fillStyle = '#9A4A3A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 25; i++) {
      ctx.fillStyle = r() > 0.5 ? '#8A3A2A' : '#AA5A4A';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 159: Terracotta orange (terracotta with orange tint)
  drawTile(159, (ctx, s, r) => {
    ctx.fillStyle = '#B87040';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 25; i++) {
      ctx.fillStyle = r() > 0.5 ? '#A86030' : '#C88050';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // ===== Decorative blocks (Fase 8) =====

  // Tile 160: Wool red (solid red with textile texture)
  drawTile(160, (ctx, s, r) => {
    ctx.fillStyle = '#B02020';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#A01818' : '#C02828';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 161: Wool blue (solid blue with textile texture)
  drawTile(161, (ctx, s, r) => {
    ctx.fillStyle = '#2040B0';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#1838A0' : '#2848C0';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 162: Wool green (solid green with textile texture)
  drawTile(162, (ctx, s, r) => {
    ctx.fillStyle = '#30A030';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#289028' : '#38B038';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 163: Wool yellow (solid yellow with textile texture)
  drawTile(163, (ctx, s, r) => {
    ctx.fillStyle = '#D4C020';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#C4B018' : '#E4D028';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 164: Wool black (solid dark with textile texture)
  drawTile(164, (ctx, s, r) => {
    ctx.fillStyle = '#1A1A1A';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#111111' : '#252525';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 165: Wool orange (solid orange with textile texture)
  drawTile(165, (ctx, s, r) => {
    ctx.fillStyle = '#D07020';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#C06018' : '#E08028';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 166: Wool purple (solid purple with textile texture)
  drawTile(166, (ctx, s, r) => {
    ctx.fillStyle = '#7030A0';
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = r() > 0.5 ? '#602890' : '#8038B0';
      ctx.fillRect(Math.floor(r() * s), Math.floor(r() * s), 2, 1);
    }
  });

  // Tile 167: Glass red (tinted transparent red)
  drawTile(167, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(200, 40, 40, 0.45)';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(255, 80, 80, 0.5)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(255, 120, 120, 0.3)';
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(s, s);
    ctx.moveTo(s, 0); ctx.lineTo(0, s);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255, 200, 200, 0.35)';
    ctx.fillRect(1, 1, 3, 3);
  });

  // Tile 168: Glass blue (tinted transparent blue)
  drawTile(168, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(40, 60, 200, 0.45)';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(80, 100, 255, 0.5)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(120, 160, 255, 0.3)';
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(s, s);
    ctx.moveTo(s, 0); ctx.lineTo(0, s);
    ctx.stroke();
    ctx.fillStyle = 'rgba(200, 220, 255, 0.35)';
    ctx.fillRect(1, 1, 3, 3);
  });

  // Tile 169: Glass green (tinted transparent green)
  drawTile(169, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(40, 160, 40, 0.45)';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(80, 200, 80, 0.5)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(120, 220, 120, 0.3)';
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(s, s);
    ctx.moveTo(s, 0); ctx.lineTo(0, s);
    ctx.stroke();
    ctx.fillStyle = 'rgba(200, 255, 200, 0.35)';
    ctx.fillRect(1, 1, 3, 3);
  });

  // Tile 170: Glass yellow (tinted transparent yellow)
  drawTile(170, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(200, 180, 20, 0.45)';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(240, 220, 60, 0.5)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(255, 240, 100, 0.3)';
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(s, s);
    ctx.moveTo(s, 0); ctx.lineTo(0, s);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255, 255, 200, 0.35)';
    ctx.fillRect(1, 1, 3, 3);
  });

  // Tile 171: Lantern (iron frame + glowing center)
  drawTile(171, (ctx, s, r) => {
    ctx.fillStyle = '#1A1A1A';
    ctx.fillRect(0, 0, s, s);
    // Iron frame
    ctx.fillStyle = '#555555';
    ctx.fillRect(0, 0, s, 2);
    ctx.fillRect(0, s - 2, s, 2);
    ctx.fillRect(0, 0, 2, s);
    ctx.fillRect(s - 2, 0, 2, s);
    // Cross bars
    ctx.fillRect(Math.floor(s / 2) - 1, 0, 2, s);
    ctx.fillRect(0, Math.floor(s / 2) - 1, s, 2);
    // Glowing center
    ctx.fillStyle = '#FFD050';
    ctx.fillRect(3, 3, s - 6, s - 6);
    ctx.fillStyle = '#FFEE88';
    ctx.fillRect(5, 5, s - 10, s - 10);
  });

  // Tile 172: Chain (iron links vertical)
  drawTile(172, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.clearRect(0, 0, s, s);
    const cx = Math.floor(s / 2);
    // Draw chain links
    ctx.fillStyle = '#6A6A6A';
    for (let y = 0; y < s; y += 4) {
      ctx.fillRect(cx - 2, y, 4, 2);
      ctx.fillStyle = '#888888';
      ctx.fillRect(cx - 1, y + 2, 2, 2);
      ctx.fillStyle = '#6A6A6A';
    }
  });

  // Tile 173: Campfire (logs arrangement)
  drawTile(173, (ctx, s, r) => {
    ctx.fillStyle = '#5A4030';
    ctx.fillRect(0, 0, s, s);
    // Logs
    ctx.fillStyle = '#6B4226';
    ctx.fillRect(2, 4, s - 4, 3);
    ctx.fillRect(2, 9, s - 4, 3);
    ctx.fillStyle = '#8B5E3C';
    ctx.fillRect(3, 2, 3, s - 4);
    ctx.fillRect(s - 6, 2, 3, s - 4);
    // Ash center
    ctx.fillStyle = '#444444';
    ctx.fillRect(5, 5, s - 10, s - 10);
  });

  // Tile 174: Campfire lit (logs + fire)
  drawTile(174, (ctx, s, r) => {
    ctx.fillStyle = '#5A4030';
    ctx.fillRect(0, 0, s, s);
    // Logs
    ctx.fillStyle = '#6B4226';
    ctx.fillRect(2, 4, s - 4, 3);
    ctx.fillRect(2, 9, s - 4, 3);
    ctx.fillStyle = '#8B5E3C';
    ctx.fillRect(3, 2, 3, s - 4);
    ctx.fillRect(s - 6, 2, 3, s - 4);
    // Fire center
    ctx.fillStyle = '#FF6600';
    ctx.fillRect(5, 4, s - 10, s - 8);
    ctx.fillStyle = '#FFCC00';
    ctx.fillRect(6, 5, s - 12, s - 10);
    ctx.fillStyle = '#FFE060';
    ctx.fillRect(7, 6, s - 14, s - 12);
  });

  // Tile 175: Candle (small wax + flame)
  drawTile(175, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.clearRect(0, 0, s, s);
    const cx = Math.floor(s / 2);
    // Wax body
    ctx.fillStyle = '#E8D8A0';
    ctx.fillRect(cx - 2, 6, 4, s - 6);
    ctx.fillStyle = '#D4C890';
    ctx.fillRect(cx - 1, 7, 2, s - 7);
    // Wick
    ctx.fillStyle = '#333333';
    ctx.fillRect(cx, 4, 1, 3);
    // Flame
    ctx.fillStyle = '#FF8800';
    ctx.fillRect(cx - 1, 2, 3, 3);
    ctx.fillStyle = '#FFCC00';
    ctx.fillRect(cx, 1, 1, 3);
  });

  // Tile 176: Scaffolding (bamboo frame with gaps)
  drawTile(176, (ctx, s, r) => {
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.clearRect(0, 0, s, s);
    // Bamboo poles
    ctx.fillStyle = '#C4A040';
    ctx.fillRect(0, 0, 2, s);
    ctx.fillRect(s - 2, 0, 2, s);
    // Cross beams
    ctx.fillRect(0, 0, s, 2);
    ctx.fillRect(0, s - 2, s, 2);
    ctx.fillRect(0, Math.floor(s / 2) - 1, s, 2);
    // Diagonal support
    ctx.fillStyle = '#B09030';
    for (let i = 0; i < s; i += 2) {
      ctx.fillRect(i, i, 2, 1);
    }
  });

  // Tile 177: Barrel (wood planks + metal bands)
  drawTile(177, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    // Wood grain (vertical planks)
    for (let x = 0; x < s; x += 4) {
      ctx.fillStyle = x % 8 === 0 ? '#7A5A2E' : '#9C7C4E';
      ctx.fillRect(x, 0, 2, s);
    }
    // Metal bands
    ctx.fillStyle = '#888888';
    ctx.fillRect(0, 2, s, 2);
    ctx.fillRect(0, s - 4, s, 2);
    ctx.fillStyle = '#666666';
    ctx.fillRect(0, Math.floor(s / 2) - 1, s, 2);
  });

  // Tile 178: Loom (wood with cloth draped)
  drawTile(178, (ctx, s, r) => {
    ctx.fillStyle = '#8B6B3E';
    ctx.fillRect(0, 0, s, s);
    // Wooden frame
    ctx.fillStyle = '#6B4F2E';
    ctx.fillRect(0, 0, s, 3);
    ctx.fillRect(0, 0, 3, s);
    ctx.fillRect(s - 3, 0, 3, s);
    // Cloth panel (white/cream)
    ctx.fillStyle = '#E8E0D0';
    ctx.fillRect(4, 4, s - 8, s - 6);
    // Weave pattern
    ctx.fillStyle = '#D0C8B0';
    for (let y = 4; y < s - 2; y += 3) {
      ctx.fillRect(4, y, s - 8, 1);
    }
  });

  // Tile 179: Smoker (furnace variant with dark logs)
  drawTile(179, (ctx, s, r) => {
    ctx.fillStyle = '#6A6A6A';
    ctx.fillRect(0, 0, s, s);
    // Face opening
    ctx.fillStyle = '#333333';
    ctx.fillRect(4, 4, s - 8, s - 6);
    // Fire glow inside
    ctx.fillStyle = '#884422';
    ctx.fillRect(5, 5, s - 10, s - 8);
    // Smoke vent on top
    ctx.fillStyle = '#555555';
    ctx.fillRect(Math.floor(s / 2) - 2, 0, 4, 3);
    // Log pattern on sides
    ctx.fillStyle = '#5A4030';
    ctx.fillRect(0, 0, 3, s);
    ctx.fillRect(s - 3, 0, 3, s);
  });

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
